import React, { useState, useEffect, useLayoutEffect, useRef, createContext, useContext, useMemo, useCallback } from 'react';
import './App.css';
import './components/SecretsModal.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import WelcomePage from './components/WelcomePage';
import ResetPasswordPage from './components/ResetPasswordPage';
import AppNav from './components/AppNav';
import SecretsModal from './components/SecretsModal';
import AlertModal from './components/AlertModal';
import AutofillModal from './components/AutofillModal';
import AutocloseModal from './components/AutocloseModal';
import ModernDatePicker from './components/ModernDatePicker';
import { modalInputClass } from './components/modalInputClass';
import WriteOrderModal from './components/WriteOrderModal';
import WriteCloseModal from './components/WriteCloseModal';
import BrokerCloseModal from './components/BrokerCloseModal';
import DealSummaryModal from './components/DealSummaryModal';
import CreateOrderConfirmModal from './components/CreateOrderConfirmModal';
import ConfirmModal from './components/ConfirmModal';
import Toast from './components/Toast';
import ToastStack, { useToastStack } from './components/ToastStack';
import RobotCommandsTable, { RobotCommandsProvider, ROBOT_COMMAND_SELECT_EVENT } from './components/RobotCommandsTable';
import AccountRobotPage from './components/AccountRobotPage';
import StockLogo from './components/StockLogo';
import MarginIndicatorsPanel from './components/MarginIndicatorsPanel';
import { useModalSelectionGuard, preventModalOverlaySelection } from './hooks/useModalSelectionGuard';
import { clearTextSelection } from './utils/clearTextSelection';
import { captureReferralFromUrl } from './utils/referralStorage';
import authService from './services/authService';
import { ThemeProvider } from './hooks/useTheme';
import {
  formatNotificationMessage,
  isAlertTriggeredMessage,
  isMarginIndicatorsUpdateMessage,
  isNotificationMessage,
  isPlanUpdateMessage,
  isPlanUpdatePromoMessage,
  isRobotCommandsUpdateMessage,
  parseSseMessage,
} from './types/sse-messages';
import { MOBILE_LAYOUT_MEDIA_QUERY } from './constants/layout';

const SIDE_PANEL_LEFT = 'calc(50% + 492px)';
const SIDE_PANEL_BOTTOM = 'calc(50vh - 342px)';
const SIDE_PANEL_WIDTH = '280px';

// Контекст для данных SSE потока
export const PlanStreamContext = createContext({
  dividends: [],
  metadata: { keyrate: 0, fairSpreads: [] },
  loading: true,
  error: null,
  triggeredAlerts: [],
  marginIndicators: null,
  userId: null,
  isPromo: false
});

function isDefined(value) {
  return value !== null && value !== undefined;
}

function parseSearchTerms(query) {
  return query
    .split(',')
    .map((term) => term.trim().toUpperCase())
    .filter(Boolean);
}

function rowMatchesSearchTerms(stock, futuresTicker, searchTerms) {
  if (searchTerms.length === 0) {
    return false;
  }

  const searchableValues = [stock, futuresTicker];
  return searchTerms.some((term) =>
    searchableValues.some((value) =>
      String(value || '').toUpperCase().includes(term)
    )
  );
}

function getQuotePriceClass(priceInfo) {
  return !priceInfo?.isTrading ? 'closed-quote-price' : undefined;
}

const NO_DATA = 'нд';
const NULL_DATA = '-';

const SERVER_URL = window.location.hostname.includes('devtunnels.ms') 
  ? `https://${window.location.hostname}/api` 
  : '/api';

const UI_SETTINGS_STORAGE_PREFIX = 'moneyProject.uiSettings.';

function usePersistentState(storageKey, defaultValue) {
  const fullStorageKey = `${UI_SETTINGS_STORAGE_PREFIX}${storageKey}`;
  const [state, setState] = useState(() => {
    try {
      const savedValue = localStorage.getItem(fullStorageKey);
      return savedValue === null ? defaultValue : JSON.parse(savedValue);
    } catch (error) {
      console.error(`Error loading UI setting "${storageKey}":`, error);
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(fullStorageKey, JSON.stringify(state));
    } catch (error) {
      console.error(`Error saving UI setting "${storageKey}":`, error);
    }
  }, [fullStorageKey, state, storageKey]);

  return [state, setState];
}

const formatNumber = (num, digits = 2) => {
  if (num === null || num === undefined || isNaN(num)) return '-';
  return Number(num).toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

const formatPercent = (num, digits = 1) => {
  if (num === null || num === undefined || isNaN(num)) return '-';
  return Number(num).toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits }) + ' %';
};

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const DATE_VALUE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const formatDateForModalInput = (date) => {
  if (!date || !DATE_VALUE_PATTERN.test(date)) return date || '';
  const [year, month, day] = date.split('-');
  return `${day}.${month}.${year}`;
};

const isUrgentDaysLeft = (daysLeft) =>
  daysLeft != null && daysLeft >= 0 && daysLeft <= 5;

const getDaysDateCellClasses = (daysLeft) => {
  if (isUrgentDaysLeft(daysLeft)) {
    return { daysClass: 'orange-text', dateClass: 'orange-text' };
  }
  return { daysClass: 'spread-text', dateClass: '' };
};

const getSpreadResClass = (value) => {
  if (value == null || Number.isNaN(Number(value))) {
    return '';
  }
  if (value > 0) {
    return 'green-text';
  }
  if (value < 0) {
    return 'red-text';
  }
  return '';
};

const getSpreadResClassName = (value) => {
  const colorClass = getSpreadResClass(value);
  return colorClass ? `bold-text ${colorClass}` : 'bold-text';
};

const getDeltaClass = (value) => {
  if (value == null || Number.isNaN(Number(value))) {
    return '';
  }
  return value < 0 ? 'red-text' : 'green-text';
};

const normalizeDateOnly = (date) => {
  if (!date) return null;
  const normalized = new Date(date);
  if (Number.isNaN(normalized.getTime())) return null;
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const isDateOnOrAfter = (left, right) => {
  const leftDate = normalizeDateOnly(left);
  const rightDate = normalizeDateOnly(right);
  return Boolean(leftDate && rightDate && leftDate.getTime() >= rightDate.getTime());
};

const getExitCellClasses = (deal) => {
  const dividendDate = deal.dividend?.date;
  const daysClass = 'spread-text';

  if (!deal.day?.close) {
    return { daysClass, dateClass: '' };
  }

  if (dividendDate && isDateOnOrAfter(deal.day.close, dividendDate)) {
    return { daysClass, dateClass: 'green-text' };
  }

  return { daysClass, dateClass: '' };
};

const formatShortDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
};

const PLAN_TABLE_COLUMN_COUNT = 10;

const getPlanTableColumnCount = (isPromo, isPlanMobile, showFuturesColumns) => {
  if (isPromo) return 7;
  return PLAN_TABLE_COLUMN_COUNT - (isPlanMobile && !showFuturesColumns ? 2 : 0);
};

const arePlanFuturesColumnsVisible = (isPromo, isPlanMobile, showFuturesColumns) => (
  isPromo || !isPlanMobile || showFuturesColumns
);

const getPlanExpirationKey = (item) => {
  const expirationDate = item?.futures?.expirationDate;
  if (!expirationDate) {
    return 'unknown';
  }
  const date = new Date(expirationDate);
  if (Number.isNaN(date.getTime())) {
    return 'unknown';
  }
  return date.toISOString().slice(0, 10);
};

const getPlanExpirationSortValue = (item) => {
  const expirationDate = item?.futures?.expirationDate;
  if (!expirationDate) {
    return Number.POSITIVE_INFINITY;
  }
  const time = new Date(expirationDate).getTime();
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
};

const getFairSpreadForExpiration = (expirationDate, fairSpreads) => {
  if (!expirationDate || !Array.isArray(fairSpreads) || fairSpreads.length === 0) {
    return null;
  }

  const date = new Date(expirationDate);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const month = date.getMonth() + 1;
  const day = date.getDate();
  return fairSpreads.find((item) => item.month === month && item.day === day) ?? null;
};

const formatPlanExpirationDividerText = (expirationDate, expirationDaysLeft, fairSpread, compact = false) => {
  if (compact) {
    let dateText = '-';
    if (expirationDate) {
      const date = new Date(expirationDate);
      if (!Number.isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);
        dateText = `${day}.${month}.${year}`;
      }
    }
    const daysText = isDefined(expirationDaysLeft)
      ? ` (${formatNumber(expirationDaysLeft, 0)} дн.)`
      : '';
    const fairSpreadText = fairSpread
      ? ` Спред: ${formatNumber(fairSpread.value * 0.9, 1)} - ${formatNumber(fairSpread.value, 1)} %`
      : '';
    return `Экспирация ${dateText}${daysText}, ${fairSpreadText}`;
  }

  const dateText = expirationDate
    ? new Date(expirationDate).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '-';
  const daysText = isDefined(expirationDaysLeft)
    ? ` (${formatNumber(expirationDaysLeft, 0)} дн.)`
    : '';
  const fairSpreadText = fairSpread
    ? ` Справедливый спред: ${formatNumber(fairSpread.value * 0.9, 1)} - ${formatNumber(fairSpread.value, 1)} %`
    : '';

  return `Экспирация ${dateText}${daysText}${fairSpreadText}`;
};

const PLAN_NUMERIC_SORT_KEYS = new Set([
  'dividendSize',
  'dividendYield',
  'stockPrice',
  'futuresPrice',
  'inputSpread',
  'delta',
  'allPercents',
  'dividendBorderLeft',
  'expirationDaysLeft',
  'maxProfit',
  'minProfit',
  'volume',
]);

function toPlanSortNumber(value) {
  if (!isDefined(value)) {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function comparePlanSortValues(aValue, bValue, direction, { numeric = false } = {}) {
  if (numeric) {
    const aNumber = toPlanSortNumber(aValue);
    const bNumber = toPlanSortNumber(bValue);

    if (aNumber === null && bNumber === null) {
      return 0;
    }
    if (aNumber === null) {
      return 1;
    }
    if (bNumber === null) {
      return -1;
    }
    if (aNumber < bNumber) {
      return direction === 'asc' ? -1 : 1;
    }
    if (aNumber > bNumber) {
      return direction === 'asc' ? 1 : -1;
    }
    return 0;
  }

  if (aValue < bValue) {
    return direction === 'asc' ? -1 : 1;
  }
  if (aValue > bValue) {
    return direction === 'asc' ? 1 : -1;
  }
  return 0;
}

const comparePlanItemsByColumn = (a, b, sortConfig, broker) => {
  if (!sortConfig.key) {
    return 0;
  }

  let aValue;
  let bValue;

  switch (sortConfig.key) {
    case 'stock':
      aValue = a.stock.toUpperCase();
      bValue = b.stock.toUpperCase();
      break;
    case 'futures':
      aValue = a.futures.ticker.toUpperCase();
      bValue = b.futures.ticker.toUpperCase();
      break;
    case 'dividendDate':
      aValue = a.dividend?.date || new Date(0);
      bValue = b.dividend?.date || new Date(0);
      break;
    case 'dividendSize':
      aValue = a.dividend?.size || 0;
      bValue = b.dividend?.size || 0;
      break;
    case 'dividendYield':
      aValue = a[broker]?.calculatedFields?.dividendYield || 0;
      bValue = b[broker]?.calculatedFields?.dividendYield || 0;
      break;
    case 'stockPrice':
      aValue = a[broker]?.stock?.ask?.price || 0;
      bValue = b[broker]?.stock?.ask?.price || 0;
      break;
    case 'futuresPrice':
      aValue = a[broker]?.futures?.ask?.price || 0;
      bValue = b[broker]?.futures?.ask?.price || 0;
      break;
    case 'inputSpread':
      aValue = a[broker]?.calculatedFields?.inputSpread;
      bValue = b[broker]?.calculatedFields?.inputSpread;
      break;
    case 'delta':
      aValue = a[broker]?.calculatedFields?.delta || 0;
      bValue = b[broker]?.calculatedFields?.delta || 0;
      break;
    case 'allPercents':
      aValue = a[broker]?.calculatedFields?.allPercents;
      bValue = b[broker]?.calculatedFields?.allPercents;
      break;
    case 'dividendBorderLeft':
      aValue = a.futures.dividendBorderLeft || 0;
      bValue = b.futures.dividendBorderLeft || 0;
      break;
    case 'expirationDaysLeft':
      aValue = a.futures.expirationDaysLeft;
      bValue = b.futures.expirationDaysLeft;
      break;
    case 'maxProfit':
      aValue = a[broker]?.calculatedFields?.maxProfit;
      bValue = b[broker]?.calculatedFields?.maxProfit;
      break;
    case 'minProfit':
      aValue = a[broker]?.calculatedFields?.minProfit;
      bValue = b[broker]?.calculatedFields?.minProfit;
      break;
    case 'volume':
      aValue = (a[broker]?.futures?.ask?.priceSeveral || 0) * (a[broker]?.futures?.ask?.sizeSeveral || 0);
      bValue = (b[broker]?.futures?.ask?.priceSeveral || 0) * (b[broker]?.futures?.ask?.sizeSeveral || 0);
      break;
    default:
      return 0;
  }

  return comparePlanSortValues(aValue, bValue, sortConfig.direction, {
    numeric: PLAN_NUMERIC_SORT_KEYS.has(sortConfig.key),
  });
};

function normalizeSpreadHistoryPoints(history) {
  const bars = Array.isArray(history?.bars) ? history.bars : history;

  return (Array.isArray(bars) ? bars : [])
    .map((point) => {
      if (Array.isArray(point)) {
        const [date, spread] = point;
        return { date: new Date(date), spread: Number(spread) };
      }

      return {
        date: new Date(point?.time),
        spread: Number(point?.spreadValue)
      };
    })
    .filter((point) => !Number.isNaN(point.date.getTime()) && Number.isFinite(point.spread))
    .sort((left, right) => left.date.getTime() - right.date.getTime());
}

function SpreadHistoryChart({ history, entryDate, currentValue, compact = false }) {
  const historyPoints = normalizeSpreadHistoryPoints(history);
  const currentSpread = Number(currentValue);
  const hasCurrentPoint = Number.isFinite(currentSpread);
  const today = new Date();
  const isSameDate = (left, right) =>
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate();
  const getDayKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  const rawPoints = hasCurrentPoint
    ? [
        ...historyPoints,
        {
          date: today,
          spread: currentSpread,
          isCurrent: true
        }
      ]
    : historyPoints;
  const sortedPoints = [...rawPoints].sort((left, right) => left.date.getTime() - right.date.getTime());
  const pointsByDay = new Map();
  const dayOrder = [];
  for (const point of sortedPoints) {
    const dayKey = getDayKey(point.date);
    if (!pointsByDay.has(dayKey)) {
      pointsByDay.set(dayKey, []);
      dayOrder.push(dayKey);
    }
    pointsByDay.get(dayKey).push(point);
  }
  const dayIndexByKey = new Map(dayOrder.map((dayKey, index) => [dayKey, index]));
  const dayInnerWidth = 0.85;
  const points = sortedPoints.map((point) => {
    const dayKey = getDayKey(point.date);
    const dayPoints = pointsByDay.get(dayKey);
    const pointIndex = dayPoints.indexOf(point);
    const dayIndex = dayIndexByKey.get(dayKey);
    const intraDayX = dayPoints.length > 1 ? (pointIndex / (dayPoints.length - 1)) * dayInnerWidth : 0;

    return { ...point, x: dayIndex + intraDayX };
  });

  if (points.length === 0) {
    return <div className="spread-history-tooltip__empty">Нет истории спреда</div>;
  }

  const width = compact ? 300 : 320;
  const height = compact ? 118 : 150;
  const padding = compact
    ? { top: 20, right: 10, bottom: 30, left: 34 }
    : { top: 28, right: 16, bottom: 40, left: 42 };
  const tickFontSize = compact ? 8 : 10;
  const labelFontSize = compact ? 8 : 9;
  const dateLabelY = height - (compact ? 14 : 18);
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const minTime = points[0].date.getTime();
  const maxTime = points[points.length - 1].date.getTime();
  const minX = 0;
  const maxX = Math.max(dayOrder.length - 1 + dayInnerWidth, 1);
  const entryDateValue = entryDate ? new Date(entryDate) : null;
  const entryTime = entryDateValue && !Number.isNaN(entryDateValue.getTime()) ? entryDateValue.getTime() : null;
  const showEntryMarker = entryTime !== null && entryTime >= minTime && entryTime <= maxTime;
  const isEntryDate = (date) => showEntryMarker && isSameDate(date, entryDateValue);
  const entryPoint = points.find((point) => isEntryDate(point.date));
  const hasEntryPoint = Boolean(entryPoint);
  const minSpreadValue = Math.min(...points.map((point) => point.spread));
  const maxSpreadValue = Math.max(...points.map((point) => point.spread));
  const getNiceStep = (value) => {
    if (!Number.isFinite(value) || value <= 0) {
      return 1;
    }

    const exponent = Math.floor(Math.log10(value));
    const base = Math.pow(10, exponent);
    const fraction = value / base;
    const niceFraction = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;

    return niceFraction * base;
  };
  const yStepsEachSide = 2;
  const maxAbsSpread = Math.max(Math.abs(minSpreadValue), Math.abs(maxSpreadValue), 0.01);
  const yStep = getNiceStep(maxAbsSpread / yStepsEachSide);
  const maxSpread = yStep * yStepsEachSide;
  const minSpread = -maxSpread;
  const spreadRange = maxSpread - minSpread || 1;
  const xRange = maxX - minX || 1;

  const toX = (point) => padding.left + ((point.x - minX) / xRange) * chartWidth;
  const toY = (spread) => padding.top + chartHeight - ((spread - minSpread) / spreadRange) * chartHeight;
  const entryDayIndex = entryDateValue ? dayIndexByKey.get(getDayKey(entryDateValue)) : undefined;
  const entryX = showEntryMarker
    ? entryPoint
      ? toX(entryPoint)
      : entryDayIndex !== undefined
        ? padding.left + ((entryDayIndex - minX) / xRange) * chartWidth
        : null
    : null;
  const hasEntryMarker = entryX !== null;
  const historyPointsWithX = points.filter((point) => !point.isCurrent);
  const polylinePoints = historyPointsWithX.map((point) => `${toX(point)},${toY(point.spread)}`).join(' ');
  const dashedSegmentPoints =
    hasCurrentPoint && historyPointsWithX.length > 0
      ? [
          historyPointsWithX[historyPointsWithX.length - 1],
          points[points.length - 1]
        ]
          .map((point) => `${toX(point)},${toY(point.spread)}`)
          .join(' ')
      : '';
  const yTicks = Array.from({ length: yStepsEachSide * 2 + 1 }, (_, index) =>
    Number(((index - yStepsEachSide) * yStep).toFixed(2))
  );
  const firstPointByDay = new Map();
  for (const point of points) {
    const dayKey = getDayKey(point.date);
    if (!firstPointByDay.has(dayKey)) {
      firstPointByDay.set(dayKey, point);
    }
  }
  const isFirstPointOfDay = (point) => firstPointByDay.get(getDayKey(point.date)) === point;

  return (
    <div className={`spread-history-chart${compact ? ' spread-history-chart--compact' : ''}`}>
      <svg
        width={compact ? '100%' : width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="График истории спреда"
      >
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + chartHeight} stroke="var(--color-spread-chart-axis)" />
        <line x1={padding.left} y1={padding.top + chartHeight} x2={padding.left + chartWidth} y2={padding.top + chartHeight} stroke="var(--color-spread-chart-axis)" />
        {yTicks.map((tick) => (
          <g key={tick}>
            <line
              x1={padding.left}
              y1={toY(tick)}
              x2={padding.left + chartWidth}
              y2={toY(tick)}
              stroke="var(--color-spread-chart-grid)"
            />
            <text x={padding.left - 8} y={toY(tick) + 3} textAnchor="end" fill="var(--color-spread-chart-label)" fontSize={tickFontSize}>
              {formatNumber(tick, 2)}
            </text>
          </g>
        ))}
        <line
          x1={padding.left}
          y1={toY(0)}
          x2={padding.left + chartWidth}
          y2={toY(0)}
          stroke="var(--color-spread-chart-zero)"
          strokeWidth="1"
        />
        {hasEntryMarker && (
          <line
            x1={entryX}
            y1={padding.top}
            x2={entryX}
            y2={padding.top + chartHeight}
            stroke="var(--color-spread-chart-entry)"
            strokeWidth="1.5"
            strokeOpacity="0.85"
          />
        )}
        <polyline points={polylinePoints} fill="none" stroke="var(--color-spread-chart-line)" strokeWidth="2" />
        {dashedSegmentPoints && (
          <polyline
            points={dashedSegmentPoints}
            fill="none"
            stroke="var(--color-spread-chart-line)"
            strokeWidth="2"
            strokeDasharray="5 4"
          />
        )}
        {points.map((point, index) => (
          <g key={`${point.date.toISOString()}-${index}`}>
            {isFirstPointOfDay(point) && (
              <>
                <circle cx={toX(point)} cy={toY(point.spread)} r={point.isCurrent ? "3" : "2.5"} fill={point.isCurrent ? "var(--color-spread-chart-point-current)" : "var(--color-spread-chart-point)"} />
                <text
                  x="0"
                  y="0"
                  transform={`translate(${toX(point)} ${toY(point.spread) - 15}) rotate(-90)`}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="var(--color-spread-chart-value-label)"
                  fontSize={labelFontSize}
                >
                  {formatNumber(point.spread, 2)}
                </text>
              </>
            )}
          </g>
        ))}
        {points
          .filter((point) => !point.isCurrent && isFirstPointOfDay(point))
          .map((point, index) => (
            <text
              key={`date-${point.date.toISOString()}-${index}`}
              x="0"
              y="0"
              transform={`translate(${toX(point)} ${dateLabelY}) rotate(-90)`}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={isEntryDate(point.date) ? 'var(--color-spread-chart-entry)' : 'var(--color-spread-chart-label)'}
              fontSize={labelFontSize}
            >
              {formatShortDate(point.date)}
            </text>
          ))}
        {hasEntryMarker && !hasEntryPoint && (
          <text
            x="0"
            y="0"
            transform={`translate(${entryX} ${dateLabelY}) rotate(-90)`}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--color-spread-chart-entry)"
            fontSize="9"
          >
            {formatShortDate(entryDateValue)}
          </text>
        )}
      </svg>
    </div>
  );
}

function getDealFetchType(showOpen, showClosed) {
  if (showOpen && showClosed) return 'all';
  if (showOpen) return 'opened';
  if (showClosed) return 'closed';
  return 'opened';
}

function getDealsEmptyMessage(showOpen, showClosed) {
  if (showOpen && !showClosed) return 'Нет открытых сделок. Смените фильтр справа сверху (для показа заверешнных) или войдите в новую.';
  if (!showOpen && showClosed) return 'Нет завершенных сделок. Смените фильтр справа сверху (для показа открытых) или завершите открытую.';
  return 'Нет сделок. Совершите свою первую сделку.';
}

/** Видимые сделки по флажкам «открытые / закрытые» — общая логика для таблицы, группировки и свёрнутых групп */
function filterDealsByOpenClosed(deals, showOpen, showClosed) {
  return deals.filter((deal) => {
    const isClosed = Boolean(deal.day.close);
    if (showOpen && showClosed) return true;
    if (!showOpen && !showClosed) return false;
    if (showOpen && !isClosed) return true;
    if (showClosed && isClosed) return true;
    return false;
  });
}

const getDealCalc = (deal) => deal?.calculatedFields || {};

const getNestedValue = (obj, path) => {
  if (!path) return undefined;
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
};

const DEAL_CALC_SORT_KEYS = new Set([
  'expirationDaysLeft',
  'dividendBorderLeft',
  'dividendYield',
  'daysLong',
  'delta',
  'currentSumma',
  'pctToDO',
  'pctToExp',
]);

function isOpenDealPastExpiration(deal) {
  const calc = getDealCalc(deal);
  return !deal.day?.close && calc.expirationDaysLeft < 0;
}

function isPlanDODealVisible(deal) {
  const calc = getDealCalc(deal);
  return deal.dividend && ((!deal.day?.close && calc.dividendBorderLeft > 0) || deal.day?.close);
}

function AccountRobotRoute() {
  const { marginIndicators } = useContext(PlanStreamContext);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY).matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY);
    const handleChange = (event) => setIsMobile(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  if (!isMobile) {
    return <Navigate to="/" replace />;
  }

  return <AccountRobotPage marginIndicators={marginIndicators} />;
}

const DEAL_ROW_LONG_PRESS_MS = 450;

function DealsTable() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userId } = useContext(PlanStreamContext);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = usePersistentState('deals.sortConfig', { key: null, direction: 'asc' });
  // Новые состояния для фильтрации
  const [showOpen, setShowOpen] = usePersistentState('deals.showOpen', true); // открытый замок — показывать открытые сделки
  const [showClosed, setShowClosed] = usePersistentState('deals.showClosed', false); // закрытый замок — показывать закрытые сделки (по умолчанию выключен)
  const [selectedRowIds, setSelectedRowIds] = useState(new Set()); // Изменено на Set для множественного выделения
  const [pressedDealRowId, setPressedDealRowId] = useState(null);
  const selectedRowIdsRef = useRef(selectedRowIds);
  const dealsMobileHandledPointersRef = useRef(new Set());
  const dealRowLongPressRef = useRef({
    timer: null,
    triggered: false,
    pointerId: null,
    rowId: null,
  });
  const [editDialog, setEditDialog] = useState({ show: false, dealId: null, field: null, value: '' });
  useModalSelectionGuard(editDialog.show);
  const [writeCloseDialog, setWriteCloseDialog] = useState({ show: false, deal: null, openDealsForFutures: [] });
  const [brokerCloseDialog, setBrokerCloseDialog] = useState({ show: false, deal: null, openDealsForFutures: [] });
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [isAutocloseModalOpen, setIsAutocloseModalOpen] = useState(false);
  const [autocloseDeal, setAutocloseDeal] = useState(null);
  const [dealSummaryDeal, setDealSummaryDeal] = useState(null);
  const [dealSummaryOpenedByHold, setDealSummaryOpenedByHold] = useState(false);
  const [autocloseInitialValues, setAutocloseInitialValues] = useState({ sum: 0, annualPercent: null, spread: null });
  // Состояния для группировки (по умолчанию: сгруппировано и свернуто)
  const [isGrouped, setIsGrouped] = usePersistentState('deals.isGrouped', true);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const hasInitializedCollapsed = useRef(false);
  const [hoveredDealSpreadItem, setHoveredDealSpreadItem] = useState(null);
  const [hoveredDealSpreadHistory, setHoveredDealSpreadHistory] = useState({
    data: null,
    loading: false,
    error: null,
    unavailable: false,
  });
  const [dealSpreadTooltipPosition, setDealSpreadTooltipPosition] = useState({ x: 0, y: 0 });
  const dealSpreadHistoryRequestIdRef = useRef(0);
  const pendingRobotDealSelectionRef = useRef(null);
  const robotExpandedGroupKeyRef = useRef(null);
  const [isDealsMobile, setIsDealsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY).matches
  );
  const dealsMobileFrozenBodyRef = useRef(null);
  const dealsMobileScrollBodyRef = useRef(null);
  // Состояние для toast уведомлений
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' });

  // Функция для показа toast уведомлений
  const showToast = (message, type = 'success') => {
    setToast({ isVisible: true, message, type });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const applyRobotCommandDealSelection = useCallback((detail) => {
    if (!detail?.stock || !detail?.futures) return;

    const normalizedBroker = String(detail.broker || '').toLowerCase();
    const matchesBroker = (deal) => String(deal.broker || '').toLowerCase() === normalizedBroker;
    const matchesInstrument = (deal) =>
      deal.stock === detail.stock && deal.futures === detail.futures && matchesBroker(deal);

    let deal = deals.find((item) => matchesInstrument(item) && !item.day?.close);
    if (!deal) {
      deal = deals.find(matchesInstrument);
    }
    if (!deal) return;

    const isClosed = Boolean(deal.day?.close);
    const isDealVisible = filterDealsByOpenClosed([deal], showOpen, showClosed).length > 0;
    if (!isDealVisible) {
      if (isClosed) {
        setShowClosed(true);
        setShowOpen(false);
      } else {
        setShowOpen(true);
        setShowClosed(false);
      }
    }

    if (isGrouped) {
      const groupKey = `${deal.broker}_${deal.futures}`;
      robotExpandedGroupKeyRef.current = groupKey;
      setCollapsedGroups((prev) => {
        if (!prev.has(groupKey)) return prev;
        const next = new Set(prev);
        next.delete(groupKey);
        return next;
      });
    }

    setSelectedRowIds(new Set([deal.id]));

    window.setTimeout(() => {
      document.querySelector(`[data-deal-row-id="${deal.id}"]`)?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }, isGrouped ? 180 : 100);
  }, [deals, isGrouped, showClosed, showOpen, setShowClosed, setShowOpen]);

  const areDealAssetsTrading = (deal) => (
    deal?.stockPrice?.isTrading && deal?.futuresPrice?.isTrading
  );

  const showDealSpreadTooltip = async (event, item) => {
    const stock = item?.stock;
    const futures = item?.futures;
    if (!stock || !futures) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 360;
    const tooltipHeight = 230;
    let x = rect.right + 10;
    let y = rect.top;

    if (x + tooltipWidth > window.innerWidth) {
      x = rect.left - tooltipWidth - 10;
      if (x < 0) {
        x = window.innerWidth - tooltipWidth - 10;
      }
    }

    if (y + tooltipHeight > window.innerHeight) {
      y = window.innerHeight - tooltipHeight - 10;
      if (y < 0) {
        y = 10;
      }
    }

    setDealSpreadTooltipPosition({ x, y });
    setHoveredDealSpreadItem(item);
    setHoveredDealSpreadHistory({ data: null, loading: true, error: null, unavailable: false });

    const requestId = dealSpreadHistoryRequestIdRef.current + 1;
    dealSpreadHistoryRequestIdRef.current = requestId;

    const result = await authService.fetchSpreadHistory(stock, futures);

    if (dealSpreadHistoryRequestIdRef.current !== requestId) {
      return;
    }

    if (result.unavailable) {
      setHoveredDealSpreadHistory({ data: null, loading: false, error: null, unavailable: true });
      return;
    }

    if (result.error) {
      setHoveredDealSpreadHistory({ data: null, loading: false, error: result.error, unavailable: false });
      return;
    }

    setHoveredDealSpreadHistory({ data: result.data, loading: false, error: null, unavailable: false });
  };

  const hideDealSpreadTooltip = () => {
    dealSpreadHistoryRequestIdRef.current += 1;
    setHoveredDealSpreadItem(null);
    setHoveredDealSpreadHistory({ data: null, loading: false, error: null, unavailable: false });
  };

  const fetchDeals = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError(null);
    try {
      const type = getDealFetchType(showOpen, showClosed);
      const res = await authService.authenticatedRequest(`${SERVER_URL}/v2/deals?type=${type}`);
      const data = await res.json();
      setDeals(data);
    } catch (e) {
      setError(e.message);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [showOpen, showClosed]);

  // Удаление выделенных сделок (DELETE /v1/deals)
  const performDeleteDeals = async (dealIds) => {
    try {
      await authService.authenticatedRequest(SERVER_URL + '/v1/deals', {
        method: 'DELETE',
        body: JSON.stringify(dealIds)
      });

      setSelectedRowIds(new Set());
      fetchDeals(true);
    } catch (error) {
      showToast(`Ошибка удаления сделок: ${error.message}`, 'error');
    }
  };

  const handleDealsPopupClick = () => {
    const selectedDeals = Array.from(selectedRowIds).map(rowId => {
      const deal = sortedDeals.find(deal => deal.id === rowId);
      return deal;
    }).filter(Boolean);

    if (selectedDeals.length === 0) return;

    setConfirmDialog({
      message: 'Подтвердите удаление сделок. Реальных продаж у брокера не будет, только удаление информации из приложения.',
      confirmLabel: 'Удалить',
      confirmVariant: 'danger',
      onConfirm: () => performDeleteDeals(selectedDeals.map(deal => deal.id))
    });
  };

  const handleBrokerCloseClick = () => {
    if (selectedRowIds.size !== 1) return;
    const selectedDeal = sortedDeals.find(deal => deal.id === Array.from(selectedRowIds)[0]);
    if (!selectedDeal || selectedDeal.day?.close || !areDealAssetsTrading(selectedDeal)) return;
    const openDealsForFutures = deals.filter(d => !d.day?.close && d.futures === selectedDeal.futures);
    setBrokerCloseDialog({ show: true, deal: selectedDeal, openDealsForFutures });
  };

  const handleBrokerCloseSubmit = async (dto) => {
    try {
      const response = await authService.authenticatedRequest(SERVER_URL + '/v2/deals/close', {
        method: 'POST',
        body: JSON.stringify(dto)
      });
      const result = await response.json();
      if (!result.ok) {
        throw new Error(result.error || 'Не удалось закрыть сделку у брокера');
      }
      setBrokerCloseDialog({ show: false, deal: null, openDealsForFutures: [] });
      setSelectedRowIds(new Set());
    } catch (error) {
      showToast(`Ошибка закрытия: ${error.message}`, 'error');
    }
  };

  useEffect(() => {
    selectedRowIdsRef.current = selectedRowIds;
  }, [selectedRowIds]);

  useEffect(() => {
    fetchDeals(true); // первая загрузка — с лоадером
    const interval = setInterval(() => fetchDeals(false), 10_000); // автообновление — без лоадера
    return () => clearInterval(interval);
  }, [fetchDeals]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY);
    const handleChange = (event) => setIsDealsMobile(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const onSelect = (event) => {
      const detail = event.detail;
      if (detail?.side !== 'exit') return;
      if (loading || deals.length === 0) {
        pendingRobotDealSelectionRef.current = detail;
        return;
      }
      applyRobotCommandDealSelection(detail);
    };

    window.addEventListener(ROBOT_COMMAND_SELECT_EVENT, onSelect);
    return () => window.removeEventListener(ROBOT_COMMAND_SELECT_EVENT, onSelect);
  }, [loading, deals, applyRobotCommandDealSelection]);

  useEffect(() => {
    if (loading || deals.length === 0 || !pendingRobotDealSelectionRef.current) return;
    applyRobotCommandDealSelection(pendingRobotDealSelectionRef.current);
    pendingRobotDealSelectionRef.current = null;
  }, [loading, deals, applyRobotCommandDealSelection]);

  useEffect(() => {
    const detail = location.state?.robotCommandSelect;
    if (!detail || detail.side !== 'exit') return;

    if (loading || deals.length === 0) {
      pendingRobotDealSelectionRef.current = detail;
    } else {
      applyRobotCommandDealSelection(detail);
    }

    navigate('.', { replace: true, state: null });
  }, [location.state, loading, deals, applyRobotCommandDealSelection, navigate]);

  const getFilteredDeals = () => filterDealsByOpenClosed(deals, showOpen, showClosed);

  // При первом открытии страницы — свернуть все группы (по ключам только видимых сделок)
  useEffect(() => {
    if (hasInitializedCollapsed.current || !isGrouped || deals.length === 0) return;
    hasInitializedCollapsed.current = true;
    const filtered = filterDealsByOpenClosed(deals, showOpen, showClosed);
    const groupKeys = new Set();
    filtered.forEach(deal => groupKeys.add(`${deal.broker}_${deal.futures}`));

    const expandedGroupKey = robotExpandedGroupKeyRef.current;
    if (expandedGroupKey) {
      groupKeys.delete(expandedGroupKey);
      robotExpandedGroupKeyRef.current = null;
    }

    setCollapsedGroups(groupKeys);
  }, [deals, isGrouped, showOpen, showClosed]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedDeals = () => {
    const filtered = getFilteredDeals();
    if (!sortConfig.key) return filtered;
    return [...filtered].sort((a, b) => {
      let aValue;
      let bValue;

      // Специальная обработка для полей "Годовых" и "Профит"
      if (sortConfig.key === 'totalOnYear') {
        // Колонка «Годовых»: нижняя строка — nTotalYearPercent
        aValue = getDealCalc(a).nTotalYearPercent;
        bValue = getDealCalc(b).nTotalYearPercent;
      } else if (sortConfig.key === 'profit') {
        // Колонка «Профит»: нижняя строка — nFinRes
        aValue = getDealCalc(a).nFinRes;
        bValue = getDealCalc(b).nFinRes;
      } else if (sortConfig.key === 'spread.open') {
        aValue = getDealCalc(a).spread?.open;
        bValue = getDealCalc(b).spread?.open;
      } else if (DEAL_CALC_SORT_KEYS.has(sortConfig.key)) {
        aValue = getDealCalc(a)[sortConfig.key];
        bValue = getDealCalc(b)[sortConfig.key];
      } else if (sortConfig.key.includes('.')) {
        aValue = getNestedValue(a, sortConfig.key);
        bValue = getNestedValue(b, sortConfig.key);
      } else {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }
      
      // Для дат
      if (sortConfig.key === 'day.open' || sortConfig.key === 'day.close') {
        aValue = aValue ? new Date(aValue) : new Date(8640000000000000); // Infinity для пустых дат
        bValue = bValue ? new Date(bValue) : new Date(8640000000000000);
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const syncDealsMobileRowHeights = useCallback(() => {
    if (!isDealsMobile) return;

    const syncRowPair = (leftRow, rightRow) => {
      if (!leftRow || !rightRow) return;
      leftRow.style.height = '';
      rightRow.style.height = '';
      const height = Math.max(leftRow.getBoundingClientRect().height, rightRow.getBoundingClientRect().height);
      const heightPx = `${height}px`;
      leftRow.style.height = heightPx;
      rightRow.style.height = heightPx;
    };

    const frozenTable = dealsMobileFrozenBodyRef.current?.closest('table');
    const scrollTable = dealsMobileScrollBodyRef.current?.closest('table');
    syncRowPair(
      frozenTable?.querySelector('thead tr'),
      scrollTable?.querySelector('thead tr')
    );

    const frozenRows = dealsMobileFrozenBodyRef.current?.querySelectorAll('tr');
    const scrollRows = dealsMobileScrollBodyRef.current?.querySelectorAll('tr');
    if (!frozenRows || !scrollRows) return;

    const rowCount = Math.min(frozenRows.length, scrollRows.length);
    for (let index = 0; index < rowCount; index += 1) {
      syncRowPair(frozenRows[index], scrollRows[index]);
    }
  }, [isDealsMobile]);

  const handleDealRowClick = useCallback((rowId, e) => {
    const normalizedRowId = Number.isFinite(Number(rowId)) ? Number(rowId) : rowId;
    clearTextSelection();

    if (e?.ctrlKey || e?.metaKey) {
      setSelectedRowIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(normalizedRowId)) {
          newSet.delete(normalizedRowId);
        } else {
          newSet.add(normalizedRowId);
        }
        return newSet;
      });
      return;
    }

    const isSelected = selectedRowIdsRef.current.has(normalizedRowId);
    setSelectedRowIds(isSelected ? new Set() : new Set([normalizedRowId]));
  }, []);

  const clearDealRowLongPress = useCallback(() => {
    const pressState = dealRowLongPressRef.current;
    if (pressState.timer) {
      clearTimeout(pressState.timer);
      pressState.timer = null;
    }
  }, []);

  useEffect(() => () => clearDealRowLongPress(), [clearDealRowLongPress]);

  const openDealSummaryById = useCallback((rowId) => {
    const deal = deals.find((item) => item.id === rowId);
    if (!deal) return;
    clearTextSelection();
    setDealSummaryOpenedByHold(true);
    setDealSummaryDeal(deal);
  }, [deals]);

  const handleDealsMobilePanePointerDown = useCallback((e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    const pressState = dealRowLongPressRef.current;
    clearDealRowLongPress();
    pressState.triggered = false;
    pressState.pointerId = null;
    pressState.rowId = null;

    const row = e.target.closest('tbody tr[data-deal-row-id]');
    if (!row) {
      setPressedDealRowId(null);
      return;
    }

    const rowIdRaw = row.dataset.dealRowId;
    const rowId = rowIdRaw != null && /^\d+$/.test(rowIdRaw) ? Number(rowIdRaw) : rowIdRaw;
    setPressedDealRowId(rowId);

    pressState.pointerId = e.pointerId;
    pressState.rowId = rowId;
    pressState.timer = window.setTimeout(() => {
      pressState.timer = null;
      pressState.triggered = true;
      setPressedDealRowId(null);
      if (typeof navigator.vibrate === 'function') {
        navigator.vibrate(12);
      }
      openDealSummaryById(rowId);
    }, DEAL_ROW_LONG_PRESS_MS);
  }, [clearDealRowLongPress, openDealSummaryById]);

  const handleDealsMobilePanePointerUp = useCallback((e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    const pressState = dealRowLongPressRef.current;
    const longPressTriggered = pressState.triggered && pressState.pointerId === e.pointerId;
    clearDealRowLongPress();
    pressState.pointerId = null;
    pressState.rowId = null;
    setPressedDealRowId(null);

    if (longPressTriggered) {
      pressState.triggered = false;
      e.preventDefault();
      return;
    }

    const row = e.target.closest('tbody tr[data-deal-row-id]');
    if (!row) {
      return;
    }

    if (dealsMobileHandledPointersRef.current.has(e.pointerId)) return;
    dealsMobileHandledPointersRef.current.add(e.pointerId);
    window.setTimeout(() => {
      dealsMobileHandledPointersRef.current.delete(e.pointerId);
    }, 400);

    const rowIdRaw = row.dataset.dealRowId;
    const rowId = rowIdRaw != null && /^\d+$/.test(rowIdRaw) ? Number(rowIdRaw) : rowIdRaw;
    handleDealRowClick(rowId, e);
  }, [clearDealRowLongPress, handleDealRowClick]);

  const handleDealsMobilePanePointerCancel = useCallback(() => {
    clearDealRowLongPress();
    dealRowLongPressRef.current.triggered = false;
    dealRowLongPressRef.current.pointerId = null;
    dealRowLongPressRef.current.rowId = null;
    setPressedDealRowId(null);
  }, [clearDealRowLongPress]);

  useLayoutEffect(() => {
    if (!isDealsMobile || loading || deals.length === 0) return undefined;

    syncDealsMobileRowHeights();

    const frozenBody = dealsMobileFrozenBodyRef.current;
    const scrollBody = dealsMobileScrollBodyRef.current;
    if (!frozenBody || !scrollBody || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      syncDealsMobileRowHeights();
    });
    observer.observe(frozenBody);
    observer.observe(scrollBody);

    return () => observer.disconnect();
  }, [
    syncDealsMobileRowHeights,
    isDealsMobile,
    loading,
    deals,
    sortConfig,
    showOpen,
    showClosed,
    isGrouped,
    collapsedGroups,
    selectedRowIds,
  ]);

  if (loading) return (
    <div className="App">
      <div className="container deals-status-container"><div className="loading">Загрузка...</div></div>
    </div>
  );
  if (error) return (
    <div className="App">
      <div className="container deals-status-container"><div className="error">Ошибка: {error}</div></div>
    </div>
  );

  const sortedDeals = getSortedDeals();
  const hasDeals = Array.isArray(deals) && deals.length > 0;

  // Функция группировки по брокеру и фьючерсу (только по тем сделкам, что уже прошли фильтр открытые/закрытые и сортировку — см. sortedDeals)
  const getGroupedDeals = (sortedRows) => {
    if (!isGrouped) return sortedRows;

    const groups = new Map();
    
    sortedRows.forEach(deal => {
      const groupKey = `${deal.broker}_${deal.futures}`;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          key: groupKey,
          broker: deal.broker,
          stock: deal.stock,
          futures: deal.futures,
          deals: []
        });
      }
      groups.get(groupKey).deals.push(deal);
    });

    // Вычисляем агрегации для каждой группы
    const groupedArray = Array.from(groups.values()).map(group => {
      const deals = group.deals;
      
      const firstCalc = getDealCalc(deals[0]);

      // Эксп. - первый ExpDaysLeft и дата экспирации
      const firstExpDaysLeft = firstCalc.expirationDaysLeft ?? null;
      const firstExpirationDate = firstCalc.expirationDate ?? null;
      
      // ДО - первый DODaysLeft и дата дивиденда
      const firstDODaysLeft = firstCalc.dividendBorderLeft ?? null;
      const firstDividendDate = deals[0]?.dividend?.date ?? null;
      
      // Выход - минимальное и максимальное количество дней в сделке
      const daysLongValues = deals.map(d => getDealCalc(d).daysLong).filter(v => v != null);
      const minDaysLong = daysLongValues.length > 0 ? Math.min(...daysLongValues) : null;
      const maxDaysLong = daysLongValues.length > 0 ? Math.max(...daysLongValues) : null;
      
      // Спреды - текущее из первой сделки + среднее между результатами спредов
      const currentSpread = firstCalc.spread?.close ?? null;
      const spreadResValues = deals.map(d => getDealCalc(d).spreadRes).filter(v => v != null && !isNaN(v));
      const avgSpreadRes = spreadResValues.length > 0 
        ? spreadResValues.reduce((sum, v) => sum + v, 0) / spreadResValues.length 
        : null;
      const spreadHistory = deals[0]?.spreadHistory ?? [];
      const entryDate = deals[0]?.day?.open ?? null;
      
      // Дельта - первое значение
      const firstDelta = firstCalc.delta || 0;
      
      // Сумма — как в строке: количество фьючей + сумма (по видимым в группе сделкам)
      const totalSumma = deals.reduce((sum, d) => sum + (getDealCalc(d).currentSumma || 0), 0);
      const totalFuturesCount = deals.reduce((sum, d) => sum + (d.futuresCount || 0), 0);
      
      // Профит — суммы nFinResAll / nFinRes (как в колонке «Профит»)
      const totalProfitAll = deals.reduce((sum, d) => sum + (getDealCalc(d).nFinResAll || 0), 0);
      const totalProfit = deals.reduce((sum, d) => sum + (getDealCalc(d).nFinRes || 0), 0);
      const hasDividensWithin = deals.some(d => getDealCalc(d).nDividensWithin);
      
      // Годовых — взвешенное среднее (вес — futuresCount) по nTotalAllYearPercent / nTotalYearPercent
      const avgNTotalAllYearPercent = totalFuturesCount > 0
        ? deals.reduce((sum, d) => sum + (getDealCalc(d).nTotalAllYearPercent ?? 0) * (d.futuresCount || 0), 0) / totalFuturesCount
        : 0;
      const avgNTotalYearPercent = totalFuturesCount > 0
        ? deals.reduce((sum, d) => sum + (getDealCalc(d).nTotalYearPercent ?? 0) * (d.futuresCount || 0), 0) / totalFuturesCount
        : 0;

      // План ДО — только по сделкам, для которых столбец заполнен в строке; вес — futuresCount
      const planDODeals = deals.filter(isPlanDODealVisible);
      const planDOFuturesCount = planDODeals.reduce((sum, d) => sum + (d.futuresCount || 0), 0);
      const weightedPctToDOSum = planDODeals.reduce(
        (sum, d) => sum + (getDealCalc(d).pctToDO ?? 0) * (d.futuresCount || 0),
        0
      );
      const avgPctToDO = planDOFuturesCount > 0 ? weightedPctToDOSum / planDOFuturesCount : 0;
      const totalProfitDO = planDODeals.reduce((sum, d) => sum + (getDealCalc(d).profitDO || 0), 0);
      const hasPlanDO = planDODeals.length > 0;

      // План эксп. — взвешенное среднее pctToExp (вес — futuresCount) и сумма profitExp
      const weightedPctToExpSum = deals.reduce(
        (sum, d) => sum + (getDealCalc(d).pctToExp ?? 0) * (d.futuresCount || 0),
        0
      );
      const avgPctToExp = totalFuturesCount > 0 ? weightedPctToExpSum / totalFuturesCount : 0;
      const totalProfitExp = deals.reduce((sum, d) => sum + (getDealCalc(d).profitExp || 0), 0);
      const hasDividend = deals.some(d => d.dividend);

      return {
        ...group,
        aggregate: {
          stock: group.stock,
          futures: group.futures, // Фьюч - первый элемент
          expirationDaysLeft: firstExpDaysLeft, // Эксп. - первый ExpDaysLeft
          expirationDate: firstExpirationDate,
          dividendBorderLeft: firstDODaysLeft, // ДО - первый DODaysLeft
          dividendDate: firstDividendDate,
          minDaysLong: minDaysLong, // Выход - минимальное
          maxDaysLong: maxDaysLong, // Выход - максимальное
          currentSpread,
          avgSpreadRes: avgSpreadRes, // Спреды - среднее
          spreadHistory,
          entryDate,
          delta: firstDelta,
          currentSumma: totalSumma,
          totalFuturesCount,
          profitAll: totalProfitAll,
          profit: totalProfit,
          hasDividensWithin: hasDividensWithin,
          avgNTotalAllYearPercent,
          avgNTotalYearPercent,
          avgPctToDO,
          totalProfitDO,
          hasPlanDO,
          avgPctToExp,
          totalProfitExp,
          hasDividend
        }
      };
    });

    return groupedArray;
  };

  // Функция для переключения свертывания группы
  const toggleGroup = (groupKey) => {
    clearTextSelection();
    const willCollapse = !collapsedGroups.has(groupKey);

    setCollapsedGroups(prev => {
      const newSet = new Set(prev);
      if (prev.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });

    if (willCollapse) {
      setSelectedRowIds(prevSelected => {
        if (prevSelected.size === 0) return prevSelected;

        const groupDealIds = new Set(
          sortedDeals
            .filter(deal => `${deal.broker}_${deal.futures}` === groupKey)
            .map(deal => deal.id)
        );
        const nextSelected = new Set(
          [...prevSelected].filter(id => !groupDealIds.has(id))
        );

        return nextSelected.size === prevSelected.size ? prevSelected : nextSelected;
      });
    }
  };

  const groupedDeals = getGroupedDeals(sortedDeals);

  const collapseAllGroups = () => {
    const groupKeys = new Set(groupedDeals.map(group => group.key));
    setCollapsedGroups(groupKeys);
    setSelectedRowIds(prevSelected => {
      if (prevSelected.size === 0) return prevSelected;
      const dealIds = new Set(sortedDeals.map(deal => deal.id));
      const nextSelected = new Set(
        [...prevSelected].filter(id => !dealIds.has(id))
      );
      return nextSelected.size === prevSelected.size ? prevSelected : nextSelected;
    });
  };

  const SortableHeader = ({ children, sortKey }) => (
    <th
      onClick={() => handleSort(sortKey)}
      className={`sortable ${sortConfig.key === sortKey ? 'active' : ''}`}
      style={{ userSelect: 'none', cursor: 'pointer' }}
      title="Кликните для сортировки"
    >
      {children}
    </th>
  );

  // Функции для работы с диалогом редактирования
  const handleSaveEdit = async () => {
    try {
      // Конвертируем значение в зависимости от типа
      let value = editDialog.value;
      if (editDialog.type === 'number') {
        value = parseInt(value) || 1; // Минимальное значение 1
      } else if (editDialog.type === 'prices') {
        // Для цен отправляем объект с двумя значениями
        value = {
          stockPrice: parseFloat(editDialog.value.stockPrice) || 0,
          futuresPrice: parseFloat(editDialog.value.futuresPrice) || 0
        };
      }

      await authService.authenticatedRequest(`${SERVER_URL}/v1/deals/${editDialog.dealId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          field: editDialog.field,
          value: value
        })
      });

      // Обновляем данные
      fetchDeals(true);
      setEditDialog({ show: false, dealId: null, field: null, value: '', type: null });
    } catch (error) {
      alert(`Ошибка обновления: ${error.message}`);
    }
  };

  const handleCancelEdit = () => {
    setEditDialog({ show: false, dealId: null, field: null, value: '', type: null });
  };

  const handleDealSummaryClick = () => {
    if (selectedRowIds.size !== 1) return;
    const selectedDeal = sortedDeals.find(deal => deal.id === Array.from(selectedRowIds)[0]);
    if (!selectedDeal) return;
    setDealSummaryOpenedByHold(false);
    setDealSummaryDeal(selectedDeal);
  };

  const handleWriteCloseClick = () => {
    if (selectedRowIds.size !== 1) return;
    const selectedDeal = sortedDeals.find(deal => deal.id === Array.from(selectedRowIds)[0]);
    if (!selectedDeal || selectedDeal.day?.close) return;
    const openDealsForFutures = deals.filter(d => !d.day?.close && d.futures === selectedDeal.futures);
    setWriteCloseDialog({ show: true, deal: selectedDeal, openDealsForFutures });
  };

  const handleWriteCloseSubmit = async (dto) => {
    const selectedDeal = writeCloseDialog.deal;
    if (!selectedDeal) return;
    try {
      const response = await authService.authenticatedRequest(SERVER_URL + '/v2/deals/write-close', {
        method: 'POST',
        body: JSON.stringify({
          broker: selectedDeal.broker,
          stock: selectedDeal.stock,
          futures: selectedDeal.futures,
          stockPrice: dto.stockPrice,
          futuresPrice: dto.futuresPrice,
          futuresCount: dto.futuresCount,
          dayClose: dto.date
        })
      });
      const result = await response.json();
      if (result?.ok === false) {
        throw new Error(result.error || 'Не удалось записать закрытие');
      }
      setWriteCloseDialog({ show: false, deal: null });
      setSelectedRowIds(new Set());
      fetchDeals(true);
      showToast('Закрытие успешно записано', 'success');
    } catch (error) {
      showToast(`Ошибка: ${error.message}`, 'error');
    }
  };

  const handleAutocloseClick = () => {
    if (selectedRowIds.size !== 1) return;
    const selectedDeal = sortedDeals.find(deal => deal.id === Array.from(selectedRowIds)[0]);
    if (!selectedDeal || selectedDeal.day?.close || !areDealAssetsTrading(selectedDeal)) return;
    const openDealsForFutures = deals.filter(d => !d.day?.close && d.futures === selectedDeal.futures);
    const totalFuturesCount = openDealsForFutures.reduce((sum, d) => sum + (d.futuresCount || 1), 0);
    const selectedCalc = getDealCalc(selectedDeal);
    setAutocloseDeal(selectedDeal);
    const annualPercent =
      selectedCalc.nTotalAllYearPercent != null && selectedCalc.nTotalAllYearPercent !== undefined
        ? selectedCalc.nTotalAllYearPercent
        : null;
    setAutocloseInitialValues({
      sum: totalFuturesCount,
      annualPercent,
      spread: selectedCalc.spread?.close ?? null
    });
    setIsAutocloseModalOpen(true);
  };

  const handleCreateAutocloseCommand = async ({ sum, annualPercent, spread }) => {
    if (!autocloseDeal) {
      showToast('Сделка не выбрана', 'error');
      return;
    }
    try {
      const body = {
        broker: autocloseDeal.broker,
        stock: autocloseDeal.stock,
        futures: autocloseDeal.futures,
        summNeed: sum,
        lotSize: getDealCalc(autocloseDeal).lotSize,
        percentDO: annualPercent ?? null,
        percentExp: null,
        spread: spread ?? null,
        side: 'exit'
      };
      await authService.authenticatedRequest(SERVER_URL + '/v1/robot/command', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      showToast('Команда автозакрытия создана', 'success');
      setIsAutocloseModalOpen(false);
      setAutocloseDeal(null);
    } catch (error) {
      showToast(`Ошибка создания команды: ${error.message}`, 'error');
    }
  };

  // Функция для рендеринга строки сделки
  const renderDealRowScrollCells = (deal, calc) => (
    <>
      <td>
        {(() => {
          const { daysClass, dateClass } = getDaysDateCellClasses(calc.expirationDaysLeft);
          const showUrgentDate = !deal.day.close;
          return (
            <>
              {!deal.day.close && (
                <span className={daysClass}>{calc.expirationDaysLeft}</span>
              )}
              {!deal.day.close && <br/>}
              <span className={showUrgentDate ? (dateClass || undefined) : undefined}>
                {formatDate(calc.expirationDate)}
              </span>
            </>
          );
        })()}
      </td>
      <td>
        {deal.dividend ? (
          (() => {
            const { daysClass, dateClass } = getDaysDateCellClasses(calc.dividendBorderLeft);
            const showUrgentDate = !deal.day.close;
            return (
              <>
                {!deal.day.close && (
                  <span className={daysClass}>{calc.dividendBorderLeft}</span>
                )}
                {!deal.day.close && <br/>}
                <span className={showUrgentDate ? (dateClass || undefined) : undefined}>
                  {formatDate(deal.dividend?.date)}
                </span>
              </>
            );
          })()
        ) : (
          NULL_DATA
        )}
      </td>
      <td>
        {deal.dividend ? (
          deal.dividend.approved ? (
            <span className="green-text"> 
              <span className="bold-text">{formatPercent(calc.dividendYield)}</span> <br/> 
              {formatNumber(deal.dividend.size)}
            </span>
          ) : (
            <span> 
              <span className="bold-text">{formatPercent(calc.dividendYield)}</span> <br/> 
              {formatNumber(deal.dividend.size)}
            </span>
          )
        ) : (
          NULL_DATA
        )}
      </td>
      <td>{formatDate(deal.day.open)}</td>
      <td>
        {(() => {
          const { daysClass, dateClass } = getExitCellClasses(deal);
          if (!deal.day?.close) {
            return <span className={daysClass}>{calc.daysLong}</span>;
          }
          return (
            <>
              <span className={daysClass}>{calc.daysLong}</span>
              <br/>
              <span className={dateClass || undefined}>
                {formatDate(deal.day.close)}
              </span>
            </>
          );
        })()}
      </td>
      <td>
        {formatNumber(deal.stockPrice.open)} <br/>
        {<span>({calc.lotSize}). {formatNumber(deal.futuresPrice.open / calc.lotSize)}</span>}
      </td>
      <td>
        <span className={deal.day?.close ? undefined : getQuotePriceClass(deal.stockPrice)}>{isDefined(deal.stockPrice.close) ? formatNumber(deal.stockPrice.close) : NO_DATA}</span> <br/>
        {<span className={deal.day?.close ? undefined : getQuotePriceClass(deal.futuresPrice)}>({calc.lotSize}). {isDefined(deal.futuresPrice.close) ? formatNumber(deal.futuresPrice.close / calc.lotSize) : NO_DATA}</span>}
      </td>
      <td
        style={{ cursor: deal.stock && deal.futures ? 'help' : 'default' }}
        onMouseEnter={(e) => showDealSpreadTooltip(e, deal)}
        onMouseLeave={hideDealSpreadTooltip}
      >
        <span className="spread-prices-line">{formatNumber(calc.spread.open)} {'>'} {formatNumber(calc.spread.close)}</span> <br/>
        <span className={getSpreadResClassName(calc.spreadRes)}>{formatPercent(calc.spreadRes, 2)}</span>
      </td>
      <td><span className={getDeltaClass(calc.delta)}>{formatPercent(calc.delta)}</span></td>
      <td>
        <span>
          {deal.futuresCount}
          <br/>
          <span className="bold-text">{formatNumber(calc.currentSumma, 0)}</span>
        </span>
      </td>
      <td>
        <span className={getDeltaClass(calc.nFinResAll)}>{formatNumber(calc.nFinResAll, 0)}</span>
        <br/>
        <span className={`bold-text ${getDeltaClass(calc.nFinRes)}`.trim()}>{formatNumber(calc.nFinRes, 0)}</span>
      </td>
      <td>
        <span className={getDeltaClass(calc.nTotalAllYearPercent)}>{formatPercent(calc.nTotalAllYearPercent)}</span>
        <br/>
        <span className={`bold-text ${getDeltaClass(calc.nTotalYearPercent)}`.trim()}>{formatPercent(calc.nTotalYearPercent)}</span>
      </td>
      <td>{isPlanDODealVisible(deal)
        ? <span className="spread-text"><span className="bold-text">{formatPercent(calc.pctToDO)}</span> <br/> {formatNumber(calc.profitDO, 0)}</span>
        : '-'}
      </td>
      <td>
        <span className={deal.dividend ? 'spread-text' : undefined}>
          <span className="bold-text">{formatPercent(calc.pctToExp)}</span>
          <br/>
          {formatNumber(calc.profitExp, 0)}
        </span>
      </td>
      <td>{deal.broker === "bcs" ? "БКС" : "Финам"}</td>
    </>
  );

  const renderDealRow = (deal, { hideFuturesInFirstColumn = false, part = 'full' } = {}) => {
                const calc = getDealCalc(deal);
                const normalizedRowId = Number.isFinite(Number(deal.id)) ? Number(deal.id) : deal.id;
                const rowClassName = [
                  selectedRowIds.has(normalizedRowId) ? 'selected-row' : '',
                  isDealsMobile && pressedDealRowId === normalizedRowId ? 'pressed-row' : '',
                  isOpenDealPastExpiration(deal) ? 'expired-open-deal-row' : '',
                  hideFuturesInFirstColumn ? 'deals-table__group-detail-row' : '',
                ].filter(Boolean).join(' ');
                const rowProps = {
                  'data-deal-row-id': normalizedRowId,
                  className: rowClassName,
                  style: { cursor: 'pointer' },
                  ...(isDealsMobile && part !== 'full'
                    ? {}
                    : { onClick: (e) => handleDealRowClick(normalizedRowId, e) }),
                };
                const frozenCell = (
                  <td className={hideFuturesInFirstColumn ? 'deals-table__group-detail-first' : undefined}>
                    {hideFuturesInFirstColumn ? '\u00A0' : deal.futures}
                  </td>
                );

                if (part === 'frozen') {
                  return <tr key={`${part}-${normalizedRowId}`} {...rowProps}>{frozenCell}</tr>;
                }
                if (part === 'scroll') {
                  return <tr key={`${part}-${normalizedRowId}`} {...rowProps}>{renderDealRowScrollCells(deal, calc)}</tr>;
                }
                return (
                  <tr key={normalizedRowId} {...rowProps}>
                    {frozenCell}
                    {renderDealRowScrollCells(deal, calc)}
                  </tr>
                );
  };

  const renderGroupHeaderScrollCells = (group, agg, hasOpenDeals) => (
    <>
      <td style={{ fontWeight: 'normal' }}>
        {hasOpenDeals && agg.expirationDaysLeft != null ? (
          <span className={getDaysDateCellClasses(agg.expirationDaysLeft).daysClass}>
            {agg.expirationDaysLeft}
          </span>
        ) : agg.expirationDate ? (
          formatDate(agg.expirationDate)
        ) : null}
      </td>
      <td style={{ fontWeight: 'normal' }}>
        {hasOpenDeals && agg.dividendBorderLeft != null ? (
          <span className={getDaysDateCellClasses(agg.dividendBorderLeft).daysClass}>
            {agg.dividendBorderLeft}
          </span>
        ) : agg.dividendDate ? (
          formatDate(agg.dividendDate)
        ) : '-'}
      </td>
      <td></td>
      <td style={{ fontWeight: 'normal' }}></td>
      <td style={{ fontWeight: 'normal' }}>
        {agg.minDaysLong != null && agg.maxDaysLong != null ? (
          agg.minDaysLong === agg.maxDaysLong ? (
            <span className="spread-text">{agg.minDaysLong}</span>
          ) : (
            <span className="spread-text">{agg.minDaysLong} .. {agg.maxDaysLong}</span>
          )
        ) : '-'}
      </td>
      <td></td>
      <td></td>
      <td
        style={{
          fontWeight: 'normal',
          cursor: agg.stock && agg.futures ? 'help' : 'default'
        }}
        onMouseEnter={(e) => showDealSpreadTooltip(e, agg)}
        onMouseLeave={hideDealSpreadTooltip}
      >
        {agg.currentSpread != null ? (
          <>
            <span className="spread-prices-line">{formatPercent(agg.currentSpread, 2)}</span>
            {agg.avgSpreadRes != null ? (
              <>
                <br />
                <span className={getSpreadResClassName(agg.avgSpreadRes)}>{formatPercent(agg.avgSpreadRes, 2)}</span>
              </>
            ) : null}
          </>
        ) : agg.avgSpreadRes != null ? (
          <span className={getSpreadResClassName(agg.avgSpreadRes)}>{formatPercent(agg.avgSpreadRes, 2)}</span>
        ) : '-'}
      </td>
      <td style={{ fontWeight: 'normal' }}>
        <span className={getDeltaClass(agg.delta)}>{formatPercent(agg.delta)}</span>
      </td>
      <td style={{ fontWeight: 'normal' }}>
        <span>
          {agg.totalFuturesCount}
          <br/>
          <span className="bold-text">{formatNumber(agg.currentSumma, 0)}</span>
        </span>
      </td>
      <td style={{ fontWeight: 'normal' }}>
        <span className={getDeltaClass(agg.profitAll)}>{formatNumber(agg.profitAll, 0)}</span>
        <br/>
        <span className={`bold-text ${getDeltaClass(agg.profit)}`.trim()}>{formatNumber(agg.profit, 0)}</span>
      </td>
      <td style={{ fontWeight: 'normal' }}>
        <span className={getDeltaClass(agg.avgNTotalAllYearPercent)}>{formatPercent(agg.avgNTotalAllYearPercent)}</span>
        <br/>
        <span className={`bold-text ${getDeltaClass(agg.avgNTotalYearPercent)}`.trim()}>{formatPercent(agg.avgNTotalYearPercent)}</span>
      </td>
      <td style={{ fontWeight: 'normal' }}>
        {agg.hasPlanDO ? (
          <span className="spread-text">
            <span className="bold-text">{formatPercent(agg.avgPctToDO)}</span>
            <br/>
            {formatNumber(agg.totalProfitDO, 0)}
          </span>
        ) : '-'}
      </td>
      <td style={{ fontWeight: 'normal' }}>
        <span className={agg.hasDividend ? 'spread-text' : undefined}>
          <span className="bold-text">{formatPercent(agg.avgPctToExp)}</span>
          <br/>
          {formatNumber(agg.totalProfitExp, 0)}
        </span>
      </td>
      <td style={{ fontWeight: 'normal' }}>
        {group.broker === "bcs" ? "БКС" : "Финам"}
      </td>
    </>
  );

  const renderGroupHeaderRow = (group, { part = 'full' } = {}) => {
    const agg = group.aggregate;
    const hasOpenDeals = group.deals.some(d => !d.day.close);
    const hasExpiredOpenDeals = group.deals.some(isOpenDealPastExpiration);
    const rowProps = {
      className: [
        'group-header',
        hasExpiredOpenDeals ? 'expired-open-deal-row' : '',
      ].filter(Boolean).join(' '),
      style: { fontWeight: 'bold', cursor: 'pointer' },
      onClick: () => toggleGroup(group.key),
    };
    const frozenCell = (
      <td style={{ fontWeight: 'normal', paddingLeft: isDealsMobile ? undefined : '20px' }}>
        {!isDealsMobile && (
          <span style={{ marginRight: '10px', fontSize: '12px', fontWeight: 'normal' }}>
            ({group.deals.length})
          </span>
        )}
        <span>{agg.futures}</span>
      </td>
    );

    if (part === 'frozen') {
      return <tr key={`${part}-${group.key}`} {...rowProps}>{frozenCell}</tr>;
    }
    if (part === 'scroll') {
      return <tr key={`${part}-${group.key}`} {...rowProps}>{renderGroupHeaderScrollCells(group, agg, hasOpenDeals)}</tr>;
    }
    return (
      <tr key={group.key} {...rowProps}>
        {frozenCell}
        {renderGroupHeaderScrollCells(group, agg, hasOpenDeals)}
      </tr>
    );
  };

  const renderDealsTableBody = (part = 'full') => {
    if (isGrouped) {
      return groupedDeals.map((group) => {
        const isCollapsed = collapsedGroups.has(group.key);
        return (
          <React.Fragment key={group.key}>
            {renderGroupHeaderRow(group, { part })}
            {!isCollapsed && group.deals.map(deal => renderDealRow(deal, {
              part,
              hideFuturesInFirstColumn: isDealsMobile && isGrouped && part === 'frozen',
            }))}
          </React.Fragment>
        );
      });
    }
    return sortedDeals.map(deal => renderDealRow(deal, { part }));
  };

  const dealsScrollHeaders = (
    <>
      <SortableHeader sortKey="expirationDaysLeft">Эксп.</SortableHeader>
      <SortableHeader sortKey="dividendBorderLeft">ДО</SortableHeader>
      <SortableHeader sortKey="dividendYield">Дивы</SortableHeader>
      <SortableHeader sortKey="day.open">Вход</SortableHeader>
      <SortableHeader sortKey="daysLong">Выход</SortableHeader>
      <SortableHeader sortKey="stockPrice.open">Акция Фьюч+</SortableHeader>
      <SortableHeader sortKey="stockPrice.close">Акция Фьюч-</SortableHeader>
      <SortableHeader sortKey="spread.open">Спреды</SortableHeader>
      <SortableHeader sortKey="delta">Дельта</SortableHeader>
      <SortableHeader sortKey="currentSumma">Сумма</SortableHeader>
      <SortableHeader sortKey="profit">Профит</SortableHeader>
      <SortableHeader sortKey="totalOnYear">Годовых</SortableHeader>
      <SortableHeader sortKey="pctToDO">План ДО</SortableHeader>
      <SortableHeader sortKey="pctToExp">План эксп.</SortableHeader>
      <SortableHeader sortKey="broker">Брокер</SortableHeader>
    </>
  );

  // --- ДОБАВЛЯЕМ ФЛАЖКИ НАД ТАБЛИЦЕЙ ---
  return (
    <div className="App">
      <div className="deals-page">
        <div className="container-deals">
        <div className="deals-filters">
          <div className="deals-group-controls">
            {isGrouped && (
              <button
                type="button"
                className="deals-collapse-all-btn"
                title="Свернуть все группы"
                aria-label="Свернуть все группы"
                onClick={collapseAllGroups}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline stroke="#8b5cf6" points="20 10 14 10 14 4" />
                  <line stroke="#6d28d9" x1="14" x2="21" y1="10" y2="3" />
                  <polyline stroke="#71717a" points="4 14 10 14 10 20" />
                  <line stroke="#6d28d9" x1="3" x2="10" y1="21" y2="14" />
                </svg>
              </button>
            )}
            <label className="deals-toggle-switch" title={isGrouped ? "Отключить группировку" : "Включить группировку"}>
              <input
                type="checkbox"
                checked={isGrouped}
                onChange={e => setIsGrouped(e.target.checked)}
                className="deals-toggle-input"
              />
              <span className="deals-toggle-slider">
                <span className="deals-toggle-icon deals-toggle-icon--group">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="#71717a" d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17"/>
                    <path stroke="#6d28d9" d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12"/>
                    <path stroke="#8b5cf6" d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z"/>
                  </svg>
                </span>
              </span>
            </label>
          </div>
          <label className="deals-toggle-switch" title="Показать открытые сделки">
            <input
              type="checkbox"
              checked={showOpen}
              onChange={e => {
                const checked = e.target.checked;
                if (!checked && !showClosed) {
                  setShowClosed(true);
                }
                setShowOpen(checked);
              }}
              className="deals-toggle-input"
            />
            <span className="deals-toggle-slider">
              <span className="deals-toggle-icon">
                <svg viewBox="0 0 14.08 14.08" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill="#71717a" d="M10.361 8.373a0.673 0.673 0 0 1 -0.674 -0.674V3.776c0 -1.323 -1.076 -2.398 -2.398 -2.398s-2.398 1.076 -2.398 2.398a0.674 0.674 0 0 1 -1.348 0C3.543 1.711 5.223 0.03 7.289 0.03s3.745 1.68 3.745 3.745v3.923a0.673 0.673 0 0 1 -0.674 0.674"/><path fill="#6d28d9" d="M11.388 13.955H3.189a1.386 1.386 0 0 1 -1.386 -1.386V7.829a1.386 1.386 0 0 1 1.386 -1.386h8.199a1.386 1.386 0 0 1 1.386 1.386v4.74a1.386 1.386 0 0 1 -1.386 1.386"/><path fill="#8b5cf6" d="M3.006 12.838h-0.07a0.396 0.396 0 0 1 -0.396 -0.396V7.956a0.396 0.396 0 0 1 0.396 -0.396h0.07a0.396 0.396 0 0 1 0.396 0.396v4.486a0.396 0.396 0 0 1 -0.396 0.396"/></svg>
              </span>
            </span>
          </label>
          <label className="deals-toggle-switch" title="Показать закрытые сделки">
            <input
              type="checkbox"
              checked={showClosed}
              onChange={e => {
                const checked = e.target.checked;
                if (!checked && !showOpen) {
                  setShowOpen(true);
                }
                setShowClosed(checked);
              }}
              className="deals-toggle-input"
            />
            <span className="deals-toggle-slider">
              <span className="deals-toggle-icon">
                <svg viewBox="0 0 14.08 14.08" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill="#71717a" d="M10.361 9.6c-0.372 0 -0.674 -0.302 -0.674 -0.674V5.003c0 -1.322 -1.076 -2.398 -2.398 -2.398 -1.322 0 -2.398 1.076 -2.398 2.398v2.282c0 0.372 -0.302 0.674 -0.674 0.674s-0.674 -0.302 -0.674 -0.674v-2.282c0 -2.065 1.68 -3.746 3.746 -3.746s3.746 1.68 3.746 3.746v3.923c0 0.372 -0.302 0.674 -0.674 0.674"/><path fill="#6d28d9" d="M11.388 13.68H3.189c-0.765 0 -1.386 -0.621 -1.386 -1.386V7.554c0 -0.765 0.621 -1.386 1.386 -1.386h8.199c0.765 0 1.386 0.621 1.386 1.386v4.74c0 0.765 -0.621 1.386 -1.386 1.386"/><path fill="#8b5cf6" d="M3.006 12.563h-0.069c-0.219 0 -0.396 -0.177 -0.396 -0.396V7.681c0 -0.219 0.177 -0.396 0.396 -0.396h0.069c0.219 0 0.396 0.177 0.396 0.396v4.486c0 0.219 -0.177 0.396 -0.396 0.396"/></svg>
              </span>
            </span>
          </label>
        </div>
        {!hasDeals ? (
          <div className="table-container deals-table-scroll deals-empty-state" role="status">
            <p className="deals-empty-state__message">{getDealsEmptyMessage(showOpen, showClosed)}</p>
          </div>
        ) : (
        <div className={`table-container deals-table-scroll${isDealsMobile ? ' deals-mobile-split' : ''}`}>
          {isDealsMobile ? (
            <div
              className="deals-mobile-split__panes"
              onPointerDown={handleDealsMobilePanePointerDown}
              onPointerUp={handleDealsMobilePanePointerUp}
              onPointerCancel={handleDealsMobilePanePointerCancel}
              onClick={(e) => e.preventDefault()}
              onSelectStart={preventModalOverlaySelection}
              onContextMenu={(e) => e.preventDefault()}
            >
              <div className="deals-mobile-split__frozen">
                <table className="dividend-table deals-table deals-table--frozen">
                  <thead>
                    <tr>
                      <SortableHeader sortKey="futures">Фьюч</SortableHeader>
                    </tr>
                  </thead>
                  <tbody ref={dealsMobileFrozenBodyRef}>
                    {renderDealsTableBody('frozen')}
                  </tbody>
                </table>
              </div>
              <div className="deals-mobile-split__scroll">
                <table className="dividend-table deals-table deals-table--scroll">
                  <thead>
                    <tr>
                      {dealsScrollHeaders}
                    </tr>
                  </thead>
                  <tbody ref={dealsMobileScrollBodyRef}>
                    {renderDealsTableBody('scroll')}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <table className="dividend-table deals-table">
              <thead>
                <tr>
                  <SortableHeader sortKey="futures">Фьюч</SortableHeader>
                  {dealsScrollHeaders}
                </tr>
              </thead>
              <tbody>
                {renderDealsTableBody('full')}
              </tbody>
            </table>
          )}
            
          {/* Всплывающие кнопки для выделенных строк в таблице сделок */}
          {selectedRowIds.size > 0 && (
            <div className="selected-rows-popup-toolbar">
              {/* Кнопка закрытия сделок - только для одной открытой сделки */}
              {(() => {
                const selectedDeals = Array.from(selectedRowIds).map(rowId => 
                  sortedDeals.find(deal => deal.id === rowId)
                ).filter(Boolean);
                
                const hasOpenDeals = selectedDeals.some(deal => !deal.day?.close);
                const isSingleSelection = selectedRowIds.size === 1;
                const areAssetsTrading = isSingleSelection && areDealAssetsTrading(selectedDeals[0]);
                const isEnabled = hasOpenDeals && isSingleSelection && areAssetsTrading;
                
                const closeTitle = isEnabled
                  ? "Закрыть у брокера разово"
                  : !isSingleSelection
                    ? "Выберите одну строку"
                    : !hasOpenDeals
                      ? "Выберите открытую сделку"
                      : "Оба актива должны торговаться";
                
                return (
                  <div 
                    className={`selected-rows-popup ${!isEnabled ? 'selected-rows-popup--disabled' : ''}`}
                    onClick={isEnabled ? handleBrokerCloseClick : undefined}
                    title={closeTitle}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </div>
                );
              })()}
              {(() => {
                const selectedDeal = selectedRowIds.size === 1
                  ? sortedDeals.find(deal => deal.id === Array.from(selectedRowIds)[0])
                  : null;
                const isAutocloseEnabled = selectedDeal && !selectedDeal.day?.close && areDealAssetsTrading(selectedDeal);
                const autocloseTitle = isAutocloseEnabled
                  ? "Автозакрытие"
                  : selectedRowIds.size !== 1
                    ? "Выберите одну строку"
                    : selectedDeal?.day?.close
                      ? "Выберите открытую сделку"
                      : "Оба актива должны торговаться";
                return (
                  <div 
                    className={`selected-rows-popup ${!isAutocloseEnabled ? 'selected-rows-popup--disabled' : ''}`}
                    onClick={isAutocloseEnabled ? handleAutocloseClick : undefined}
                    title={autocloseTitle}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                  </div>
                );
              })()}
              {(() => {
                const selectedDeal = selectedRowIds.size === 1
                  ? sortedDeals.find(deal => deal.id === Array.from(selectedRowIds)[0])
                  : null;
                const isWriteCloseEnabled = selectedDeal && !selectedDeal.day?.close;
                return (
                  <div 
                    className={`selected-rows-popup ${!isWriteCloseEnabled ? 'selected-rows-popup--disabled' : ''}`}
                    onClick={isWriteCloseEnabled ? handleWriteCloseClick : undefined}
                    title={isWriteCloseEnabled ? "Записать закрытие" : selectedRowIds.size !== 1 ? "Выберите одну строку" : "Выберите открытую сделку"}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 11 12 14 22 4" />
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                    </svg>
                  </div>
                );
              })()}
              <div
                className="selected-rows-popup"
                onClick={handleDealsPopupClick}
                title="Удалить сделки по выделенным строкам"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </div>
              {!isDealsMobile && (() => {
                const isSummaryEnabled = selectedRowIds.size === 1;
                return (
                  <div
                    className={`selected-rows-popup selected-rows-popup--summary ${!isSummaryEnabled ? 'selected-rows-popup--disabled' : ''}`}
                    role="button"
                    onClick={isSummaryEnabled ? handleDealSummaryClick : undefined}
                    title={isSummaryEnabled ? 'Карточка сделки' : 'Выберите одну строку'}
                    aria-label="Карточка сделки"
                  >
                    <img src="/icons/summary-logo.png" alt="" width="20" height="20" />
                  </div>
                );
              })()}
            </div>
          )}
        </div>
        )}
        </div>

        {!isDealsMobile && <RobotCommandsTable variant="embedded" />}
      </div>

      {/* Диалог редактирования */}
      {editDialog.show && (
        <div className="edit-dialog-overlay" onClick={handleCancelEdit} onSelectStart={preventModalOverlaySelection}>
          <div className="edit-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="edit-dialog-header">
              <h3>
                {editDialog.type === 'date' ? 'Редактирование даты' :
                 editDialog.type === 'number' ? 'Редактирование количества фьючерсов' :
                 editDialog.type === 'prices' ? 'Редактирование цен акции и фьючерса' : 'Редактирование'}
              </h3>
            </div>
            <div className="edit-dialog-content">
              {editDialog.type === 'date' ? (
                <ModernDatePicker 
                  value={editDialog.value}
                  onChange={(date) => setEditDialog(prev => ({ ...prev, value: date }))}
                />
              ) : editDialog.type === 'number' ? (
                <ModernNumberPicker 
                  value={editDialog.value}
                  onChange={(value) => setEditDialog(prev => ({ ...prev, value }))}
                  min={1}
                  step={1}
                />
              ) : editDialog.type === 'prices' ? (
                <ModernPricesPicker 
                  value={editDialog.value}
                  onChange={(value) => setEditDialog(prev => ({ ...prev, value }))}
                />
              ) : (
                <input
                  type="number"
                  value={editDialog.value}
                  onChange={(e) => setEditDialog(prev => ({ ...prev, value: e.target.value }))}
                  autoFocus
                  min="1"
                  step="1"
                />
              )}
            </div>
            <div className="edit-dialog-buttons">
              <button onClick={handleSaveEdit} className="save-btn">Сохранить</button>
              <button onClick={handleCancelEdit} className="cancel-btn">Отмена</button>
            </div>
          </div>
        </div>
      )}
      <WriteCloseModal
        isOpen={writeCloseDialog.show}
        onClose={() => setWriteCloseDialog({ show: false, deal: null, openDealsForFutures: [] })}
        onSubmit={handleWriteCloseSubmit}
        deal={writeCloseDialog.deal}
        openDealsForFutures={writeCloseDialog.openDealsForFutures || []}
      />
      <BrokerCloseModal
        isOpen={brokerCloseDialog.show}
        onClose={() => setBrokerCloseDialog({ show: false, deal: null, openDealsForFutures: [] })}
        onSubmit={handleBrokerCloseSubmit}
        deal={brokerCloseDialog.deal}
        openDealsForFutures={brokerCloseDialog.openDealsForFutures || []}
      />
      <ConfirmModal
        isOpen={Boolean(confirmDialog)}
        onClose={() => setConfirmDialog(null)}
        onConfirm={confirmDialog?.onConfirm ?? (() => {})}
        title={confirmDialog?.title}
        message={confirmDialog?.message ?? ''}
        confirmLabel={confirmDialog?.confirmLabel}
        confirmVariant={confirmDialog?.confirmVariant}
      />
      <DealSummaryModal
        isOpen={Boolean(dealSummaryDeal)}
        onClose={() => {
          setDealSummaryDeal(null);
          setDealSummaryOpenedByHold(false);
        }}
        deal={dealSummaryDeal}
        userId={userId}
        ignoreOpeningGesture={dealSummaryOpenedByHold}
      />
      {isAutocloseModalOpen && autocloseDeal && (
        <AutocloseModal
          isOpen={isAutocloseModalOpen}
          onClose={() => {
            setIsAutocloseModalOpen(false);
            setAutocloseDeal(null);
          }}
          onSubmit={handleCreateAutocloseCommand}
          broker={autocloseDeal.broker}
          defaultCount={autocloseInitialValues.sum}
          defaultAnnual={autocloseInitialValues.annualPercent}
          defaultSpread={autocloseInitialValues.spread}
        />
      )}
      {hoveredDealSpreadItem && (
        <div
          className="spread-history-tooltip"
          style={{
            left: `${dealSpreadTooltipPosition.x}px`,
            top: `${dealSpreadTooltipPosition.y}px`,
          }}
        >
          {hoveredDealSpreadHistory.loading ? (
            <div className="spread-history-tooltip__loading">Загрузка истории спреда...</div>
          ) : hoveredDealSpreadHistory.error ? (
            <div className="spread-history-tooltip__error">Ошибка получения сведений.<br/>Попробуйте еще раз.</div>
          ) : hoveredDealSpreadHistory.unavailable ? (
            <div className="spread-history-tooltip__empty">Нет данных для отображения</div>
          ) : (
            <SpreadHistoryChart
              history={hoveredDealSpreadHistory.data}
              entryDate={hoveredDealSpreadItem.entryDate || hoveredDealSpreadItem.day?.open}
              currentValue={hoveredDealSpreadItem.currentSpread ?? getDealCalc(hoveredDealSpreadItem).spread?.close}
            />
          )}
        </div>
      )}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </div>
  );
}

// Современный компонент для выбора числа
function ModernNumberPicker({ value, onChange, min = 1, step = 1 }) {
  const [currentValue, setCurrentValue] = useState(parseInt(value) || min);

  const handleIncrement = () => {
    const newValue = currentValue + step;
    setCurrentValue(newValue);
    onChange(newValue.toString());
  };

  const handleDecrement = () => {
    const newValue = Math.max(currentValue - step, min);
    setCurrentValue(newValue);
    onChange(newValue.toString());
  };

  const handleInputChange = (e) => {
    const inputValue = parseInt(e.target.value) || min;
    const clampedValue = Math.max(inputValue, min);
    setCurrentValue(clampedValue);
    onChange(clampedValue.toString());
  };

  const handleQuickAdd = (quickValue) => {
    const newValue = currentValue + quickValue;
    setCurrentValue(newValue);
    onChange(newValue.toString());
  };

  const quickValues = [1, 5, 10, 25, 50, 100];

  return (
    <div className="modern-number-picker">
      <div className="number-controls">
        <button 
          className="number-btn decrement"
          onClick={handleDecrement}
          disabled={currentValue <= min}
        >
          −
        </button>
        
        <div className="number-input-container">
          <input
            type="number"
            value={currentValue}
            onChange={handleInputChange}
            min={min}
            step={step}
            className="number-input"
            autoFocus
          />
        </div>
        
        <button 
          className="number-btn increment"
          onClick={handleIncrement}
        >
          +
        </button>
      </div>
      
      <div className="quick-values">
        <div className="quick-values-label">Быстрый выбор:</div>
        <div className="quick-values-grid">
          {quickValues.map(quickValue => (
            <button
              key={quickValue}
              className="quick-value-btn"
              onClick={() => handleQuickAdd(quickValue)}
            >
              +
              {quickValue}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Современный компонент для редактирования цен
function ModernPricesPicker({ value, onChange }) {
  const [stockPrice, setStockPrice] = useState(value?.stockPrice || 0);
  const [futuresPrice, setFuturesPrice] = useState(value?.futuresPrice || 0);

  const handleStockChange = (newPrice) => {
    setStockPrice(newPrice);
    onChange({ stockPrice: newPrice, futuresPrice });
  };

  const handleFuturesChange = (newPrice) => {
    setFuturesPrice(newPrice);
    onChange({ stockPrice, futuresPrice: newPrice });
  };

  const handleQuickStock = (price) => {
    setStockPrice(price);
    onChange({ stockPrice: price, futuresPrice });
  };

  const handleQuickFutures = (price) => {
    setFuturesPrice(price);
    onChange({ stockPrice, futuresPrice: price });
  };

  const quickStockPrices = [100, 200, 500, 1000, 2000, 5000];
  const quickFuturesPrices = [100, 200, 500, 1000, 2000, 5000];

    return (
    <div className="modern-prices-picker">
      <div className="prices-header">
        <div className="price-section">
          <div className="price-input-container">
            <input
              type="number"
              value={stockPrice}
              onChange={(e) => handleStockChange(parseFloat(e.target.value) || 0)}
              className="price-input"
              placeholder="0.00"
              step="0.01"
              min="0"
              autoFocus
            />
          </div>
          <div className="quick-prices">
            <div className="quick-prices-label">Быстрый выбор:</div>
            <div className="quick-prices-grid">
              {quickStockPrices.map(price => (
                <button
                  key={price}
                  className="quick-price-btn"
                  onClick={() => handleQuickStock(stockPrice + price)}
                >
                  +{formatNumber(price, 0)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="price-divider"></div>

        <div className="price-section">
          <div className="price-input-container">
            <input
              type="number"
              value={futuresPrice}
              onChange={(e) => handleFuturesChange(parseFloat(e.target.value) || 0)}
              className="price-input"
              placeholder="0.00"
              step="1"
              min="0"
            />
          </div>
          <div className="quick-prices">
            <div className="quick-prices-label">Быстрый выбор:</div>
            <div className="quick-prices-grid">
              {quickFuturesPrices.map(price => (
                <button
                  key={price}
                  className="quick-price-btn"
                  onClick={() => handleQuickFutures(futuresPrice + price)}
                >
                  +{formatNumber(price, 0)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Компонент боковой панели для создания заказа
function CreateOrderPanel({
  stock,
  futures,
  stockPrice,
  futuresPrice,
  stockIsTrading,
  futuresIsTrading,
  lotSize,
  broker,
  onSubmit,
  percentDo,
  percentExp,
  spread,
  onAutofillClick,
  isRowSelected = false
}) {
  const [customAmount, setCustomAmount] = useState('');
  const [pendingOrder, setPendingOrder] = useState(null);
  const hasSelectedRowContext = isRowSelected && Boolean(stock && futures && stockPrice && futuresPrice && lotSize);
  const isCustomAmountInputEnabled = hasSelectedRowContext;
  const areQuickBuyAssetsTrading = stockIsTrading && futuresIsTrading;

  // Расчет количества фьючерсов
  const calculateFuturesCount = (summa) => {
    if (!summa || !stockPrice || stockPrice <= 0 || !lotSize || lotSize <= 0) return 0;
    const sum = parseInt(summa) || 0;
    if (sum <= 0) return 0;
    // Рассчитываем количество фьючерсов: сумма / (стоимость акции * lotSize)
    return Math.floor(sum / (stockPrice * lotSize));
  };

  // Расчет стоимости в рублях после округления
  const calculateRubles = (futuresCount) => {
    if (!futuresCount || futuresCount <= 0 || !stockPrice || stockPrice <= 0 || !lotSize || lotSize <= 0) return 0;
    return futuresCount * stockPrice * lotSize;
  };

  const amounts = [30000, 100000, 300000, 500000];

  const handleAmountClick = (amount) => {
    const futuresCount = calculateFuturesCount(amount);
    if (futuresCount <= 0) {
      alert('На данную сумму нельзя купить ни одного фьючерса');
      return;
    }
    
    setPendingOrder({
      amount,
      futuresCount,
      actualCost: calculateRubles(futuresCount)
    });
  };

  const handleConfirmOrder = () => {
    if (!pendingOrder) return;
    onSubmit(broker, pendingOrder.amount, pendingOrder.futuresCount);
    setPendingOrder(null);
  };

  // Проверка валидности введенной суммы
  const isValidCustomAmount = () => {
    if (!customAmount || customAmount.trim() === '') return false;
    const amount = parseInt(customAmount.replace(/\s/g, ''), 10);
    if (isNaN(amount) || amount <= 0) return false;
    // Проверяем, можно ли купить хотя бы один фьючерс на эту сумму
    const futuresCount = calculateFuturesCount(amount);
    return futuresCount > 0;
  };

  const handleCustomAmountSubmit = () => {
    if (!isValidCustomAmount()) {
      alert('Введите корректную сумму');
      return;
    }
    const amount = parseInt(customAmount.replace(/\s/g, ''), 10);
    handleAmountClick(amount);
  };

  const formatCustomAmountWithSpaces = (val) => {
    const digits = String(val || '').replace(/[^\d]/g, '');
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    setCustomAmount(value);
  };

  const isCustomAmountSubmitEnabled = isValidCustomAmount() && areQuickBuyAssetsTrading;
  const quoteLabelColor = 'var(--color-data-cyan)';

  return (
    <div className="side-panel side-panel--quick-buy" style={{
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    }}>
      {/* Текущие стоимости */}
      <div className="quick-buy-quotes">
        <div>
          <span style={{ color: quoteLabelColor, fontSize: '0.95rem', fontWeight: 'bold' }}>
            {stock || '-'}: {hasSelectedRowContext ? `${formatNumber(stockPrice)} ₽` : '-'}
          </span>
        </div>
        <div>
          <span style={{ color: quoteLabelColor, fontSize: '0.95rem', fontWeight: 'bold' }}>
            {futures || '-'}: {hasSelectedRowContext ? `${formatNumber(futuresPrice)} ₽` : '-'}
          </span>
        </div>
      </div>

      {/* Кнопки с суммами */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {amounts.map((amount) => {
          const futuresCount = calculateFuturesCount(amount);
          const rubles = calculateRubles(futuresCount);
          const isDisabled = futuresCount <= 0 || !areQuickBuyAssetsTrading;
          
          return (
            <div key={amount} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <button
                type="button"
                className="quick-buy-btn"
                onClick={() => handleAmountClick(amount)}
                disabled={isDisabled}
              >
                {formatNumber(amount, 0)} ₽
              </button>
              <div style={{ fontSize: '0.75rem', color: '#666', textAlign: 'center' }}>
                <div>{futuresCount} на {formatNumber(futuresCount > 0 ? rubles : amount, 0)} ₽</div>
              </div>
            </div>
          );
        })}
        
        {/* Поле для ввода своей суммы */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch', width: '100%' }}>
            <input
              type="text"
              inputMode="numeric"
              className="quick-buy-input"
              value={formatCustomAmountWithSpaces(customAmount)}
              onChange={handleCustomAmountChange}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCustomAmountSubmit();
                }
              }}
              placeholder="Введите сумму"
              disabled={!isCustomAmountInputEnabled}
            />
            <button
              type="button"
              className="quick-buy-btn quick-buy-btn--compact"
              onClick={handleCustomAmountSubmit}
              disabled={!isCustomAmountSubmitEnabled}
            >
              ₽
            </button>
          </div>
          <div style={{ 
            fontSize: '0.75rem', 
            color: '#666', 
            textAlign: 'center',
            minHeight: '18px',
            visibility: isValidCustomAmount() ? 'visible' : 'hidden'
          }}>
            {isValidCustomAmount() && (() => {
              const amount = parseInt(customAmount.replace(/\s/g, ''), 10);
              const futuresCount = calculateFuturesCount(amount);
              const rubles = calculateRubles(futuresCount);
              return (
                <div>{futuresCount} на {formatNumber(rubles, 0)} ₽</div>
              );
            })()}
          </div>
        </div>

        {/* Кнопка робота */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <button
            type="button"
            className="quick-buy-btn"
            onClick={() => {
              if (isValidCustomAmount()) {
                const amount = parseInt(customAmount.replace(/\s/g, ''), 10);
                onAutofillClick(amount, percentDo, percentExp, spread);
              }
            }}
            disabled={!isValidCustomAmount()}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: '6px' }}>
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            Автонабор
          </button>
          <div style={{ fontSize: '0.75rem', color: '#666', textAlign: 'center', minHeight: '18px' }} />
        </div>
      </div>

      <CreateOrderConfirmModal
        isOpen={Boolean(pendingOrder)}
        onClose={() => setPendingOrder(null)}
        onConfirm={handleConfirmOrder}
        amount={pendingOrder?.amount}
        futuresCount={pendingOrder?.futuresCount}
        actualCost={pendingOrder?.actualCost}
        broker={broker}
        stock={stock}
        futures={futures}
      />
    </div>
  );
}

function AppTable() {
  // Используем данные из контекста SSE потока
  const { dividends, metadata, loading, error, triggeredAlerts, marginIndicators, userId, isPromo } = useContext(PlanStreamContext);
  const location = useLocation();
  const navigate = useNavigate();
  
  const [sortConfig, setSortConfig] = usePersistentState('plan.sortConfig', { key: null, direction: 'asc' });
  const [selectedRowId, setSelectedRowId] = useState(null); // Только одно выделение
  const [searchQuery, setSearchQuery] = usePersistentState('plan.searchQuery', '');
  const [showApprovedDividendsOnly, setShowApprovedDividendsOnly] = usePersistentState('plan.showApprovedDividendsOnly', false);
  const [showProfitWithoutDividends, setShowProfitWithoutDividends] = usePersistentState('plan.showProfitWithoutDividends', false);
  const [showExitSpread, setShowExitSpread] = usePersistentState('plan.showExitSpread', false);
  const [showExpirationSections, setShowExpirationSections] = usePersistentState('plan.showExpirationSections', false);
  const [showStockLogos, setShowStockLogos] = usePersistentState(
    'plan.showStockLogos',
    typeof window !== 'undefined' && !window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY).matches,
  );
  const [showFuturesColumns, setShowFuturesColumns] = usePersistentState('plan.showFuturesColumns', false);
  const [isPlanMobile, setIsPlanMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY).matches
  );
  // Состояние для выбранного брокера с сохранением в localStorage
  const [selectedBroker, setSelectedBroker] = usePersistentState(
    'plan.selectedBroker',
    localStorage.getItem('selectedBroker') ?? 'bcs'
  );

  useEffect(() => {
    if (!selectedBroker) {
      setSelectedBroker('bcs');
    }
  }, [selectedBroker, setSelectedBroker]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY);
    const handleChange = (event) => setIsPlanMobile(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (!isPlanMobile) {
      setIsMobileOrderPanelOpen(false);
    }
  }, [isPlanMobile]);

  useEffect(() => {
    const pressState = planRowLongPressRef.current;
    return () => {
      if (pressState.timer) {
        clearTimeout(pressState.timer);
      }
    };
  }, []);

  // Состояние для tooltip с margin
  const [hoveredMarginItemId, setHoveredMarginItemId] = useState(null);
  const [hoveredSpreadItem, setHoveredSpreadItem] = useState(null);
  const [hoveredSpreadHistory, setHoveredSpreadHistory] = useState({
    data: null,
    loading: false,
    error: null,
    unavailable: false,
  });
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const spreadHistoryHoverKeyRef = useRef(null);
  // Состояние для модального окна алерта
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [alertInitialValues, setAlertInitialValues] = useState({
    stock: '',
    futures: '',
    stockPrice: null,
    futuresPrice: null,
    maxProfit: null,
    minProfit: null,
    inputSpread: null,
  });
  // Состояние для модального окна автонабора
  const [isAutofillModalOpen, setIsAutofillModalOpen] = useState(false);
  const [autofillInitialValues, setAutofillInitialValues] = useState({
    sum: 0,
    percentDo: null,
    percentExp: null,
    spread: null
  });
  const [isWriteOrderModalOpen, setIsWriteOrderModalOpen] = useState(false);
  // Состояние для подсвеченных строк (сработавшие алерты)
  const [highlightedRows, setHighlightedRows] = useState(new Set());
  // Ref для отслеживания уже обработанных алертов (чтобы звук не воспроизводился дважды)
  const processedAlertsRef = useRef(new Set());
  // Состояние для toast уведомлений
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' });
  // Ref для хранения текущего selectedRowId, чтобы не добавлять его в зависимости useEffect
  const selectedRowIdRef = useRef(selectedRowId);
  const planTableHeadRef = useRef(null);
  const planTableContainerRef = useRef(null);
  const [planTableHeadHeight, setPlanTableHeadHeight] = useState(46);
  const planTableHeadHeightRef = useRef(46);
  const updatePinnedDividerRef = useRef(null);
  const [pinnedExpirationDividerText, setPinnedExpirationDividerText] = useState(null);
  const [hiddenExpirationDividerLabels, setHiddenExpirationDividerLabels] = useState(() => new Set());
  const pendingRobotPlanSelectionRef = useRef(null);
  const [isMobileOrderPanelOpen, setIsMobileOrderPanelOpen] = useState(false);

  useModalSelectionGuard(isMobileOrderPanelOpen);
  useModalSelectionGuard(isPlanMobile && Boolean(hoveredMarginItemId));
  useModalSelectionGuard(isPlanMobile && Boolean(hoveredSpreadItem));
  const planRowLongPressRef = useRef({ timer: null, triggered: false });

  // Функция для показа toast уведомлений
  const showToast = (message, type = 'success') => {
    setToast({ isVisible: true, message, type });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const applyRobotCommandPlanSelection = useCallback((detail) => {
    if (!detail?.stock || !detail?.futures) return;

    const rowId = `${detail.stock}.${detail.futures}`;
    setSelectedRowId(rowId);
    if (detail.broker) {
      setSelectedBroker(String(detail.broker).toLowerCase());
    }

    requestAnimationFrame(() => {
      setTimeout(() => {
        document.querySelector(`[data-plan-row-id="${rowId}"]`)?.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }, 0);
    });
  }, [setSelectedBroker]);

  const getSpreadHistoryKey = (item) => {
    const stock = item?.stock;
    const futures = item?.futures?.ticker;
    return stock && futures ? `${stock}-${futures}` : null;
  };

  const loadSpreadHistoryForItem = useCallback(async (item) => {
    const cacheKey = getSpreadHistoryKey(item);
    if (!cacheKey) {
      return;
    }

    spreadHistoryHoverKeyRef.current = cacheKey;
    setHoveredSpreadItem(item);
    setHoveredSpreadHistory({ data: null, loading: true, error: null, unavailable: false });

    const result = await authService.fetchSpreadHistory(item.stock, item.futures.ticker);

    if (spreadHistoryHoverKeyRef.current !== cacheKey) {
      return;
    }

    if (result.unavailable) {
      setHoveredSpreadHistory({ data: null, loading: false, error: null, unavailable: true });
      return;
    }

    if (result.error) {
      setHoveredSpreadHistory({ data: null, loading: false, error: result.error, unavailable: false });
      return;
    }

    setHoveredSpreadHistory({ data: result.data, loading: false, error: null, unavailable: false });
  }, []);

  const hideSpreadHistoryTooltip = useCallback(() => {
    spreadHistoryHoverKeyRef.current = null;
    setHoveredSpreadItem(null);
    setHoveredSpreadHistory({ data: null, loading: false, error: null, unavailable: false });
  }, []);

  const openDesktopSpreadHistoryTooltip = useCallback(async (event, item) => {
    const cacheKey = getSpreadHistoryKey(item);
    if (!cacheKey) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 360;
    const tooltipHeight = 230;
    let x = rect.right + 10;
    let y = rect.top;

    if (x + tooltipWidth > window.innerWidth) {
      x = rect.left - tooltipWidth - 10;
      if (x < 0) {
        x = window.innerWidth - tooltipWidth - 10;
      }
    }

    if (y + tooltipHeight > window.innerHeight) {
      y = window.innerHeight - tooltipHeight - 10;
      if (y < 0) {
        y = 10;
      }
    }

    setTooltipPosition({ x, y });
    await loadSpreadHistoryForItem(item);
  }, [loadSpreadHistoryForItem]);

  // Обработчик изменения брокера
  const handleBrokerChange = (broker) => {
    void broker;
    void setSelectedBroker;
    // Временно отключено: переключатель брокера только для чтения.
    // setSelectedBroker(broker);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Обновляем ref при изменении selectedRowId
  useEffect(() => {
    selectedRowIdRef.current = selectedRowId;
  }, [selectedRowId]);

  useEffect(() => {
    const onSelect = (event) => {
      const detail = event.detail;
      if (detail?.side !== 'enter') return;
      if (loading || !dividends?.length) {
        pendingRobotPlanSelectionRef.current = detail;
        return;
      }
      applyRobotCommandPlanSelection(detail);
    };

    window.addEventListener(ROBOT_COMMAND_SELECT_EVENT, onSelect);
    return () => window.removeEventListener(ROBOT_COMMAND_SELECT_EVENT, onSelect);
  }, [loading, dividends, applyRobotCommandPlanSelection]);

  useEffect(() => {
    if (loading || !dividends?.length || !pendingRobotPlanSelectionRef.current) return;
    applyRobotCommandPlanSelection(pendingRobotPlanSelectionRef.current);
    pendingRobotPlanSelectionRef.current = null;
  }, [loading, dividends, applyRobotCommandPlanSelection]);

  useEffect(() => {
    const detail = location.state?.robotCommandSelect;
    if (!detail || detail.side !== 'enter') return;

    if (loading || !dividends?.length) {
      pendingRobotPlanSelectionRef.current = detail;
    } else {
      applyRobotCommandPlanSelection(detail);
    }

    navigate('.', { replace: true, state: null });
  }, [location.state, loading, dividends, applyRobotCommandPlanSelection, navigate]);

  useEffect(() => {
    const container = planTableContainerRef.current;
    const thead = planTableHeadRef.current;
    if (!container || !thead) {
      return undefined;
    }

    const updateHeadHeight = () => {
      const headerRow = thead.rows[0];
      if (!headerRow) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const wrapperRect = container.parentElement?.getBoundingClientRect() ?? containerRect;
      const headerRect = headerRow.getBoundingClientRect();
      const theadRect = thead.getBoundingClientRect();
      const headerBottom = Math.max(headerRect.bottom, theadRect.bottom);
      const stickyTop = headerBottom - wrapperRect.top;
      const nextHeight = Math.max(0, Math.floor(stickyTop));
      planTableHeadHeightRef.current = nextHeight;
      setPlanTableHeadHeight(nextHeight);
    };

    updateHeadHeight();
    requestAnimationFrame(() => {
      requestAnimationFrame(updateHeadHeight);
    });

    const resizeObserver = new ResizeObserver(updateHeadHeight);
    resizeObserver.observe(container);
    resizeObserver.observe(thead);
    window.addEventListener('resize', updateHeadHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeadHeight);
    };
  }, [loading, showExpirationSections, dividends?.length]);

  useEffect(() => {
    const container = planTableContainerRef.current;
    if (!container || !showExpirationSections) {
      setPinnedExpirationDividerText(null);
      setHiddenExpirationDividerLabels(new Set());
      return undefined;
    }

    const updatePinnedDivider = () => {
      const thead = planTableHeadRef.current;
      const headerRow = thead?.rows?.[0];
      if (!headerRow) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const dividers = Array.from(
        container.querySelectorAll('tr.expiration-section-divider[data-section-label]')
      );

      if (dividends.length === 0) {
        setPinnedExpirationDividerText(null);
        setHiddenExpirationDividerLabels(new Set());
        return;
      }

      const getRowScrollOffset = (row) =>
        row.getBoundingClientRect().top - containerRect.top + container.scrollTop;

      const pinScrollOffset = container.scrollTop + planTableHeadHeightRef.current;
      let activeIndex = 0;
      dividers.forEach((row, index) => {
        if (getRowScrollOffset(row) <= pinScrollOffset + 1) {
          activeIndex = index;
        }
      });

      const activeRow = dividers[activeIndex];
      const activeLabel = activeRow?.dataset.sectionLabel ?? null;
      const shouldPin = container.scrollTop > 0 && Boolean(activeLabel);
      const nextPinnedText = shouldPin ? activeLabel : null;
      const nextHiddenLabels = shouldPin && activeLabel ? new Set([activeLabel]) : new Set();

      setPinnedExpirationDividerText((prev) => (prev === nextPinnedText ? prev : nextPinnedText));
      setHiddenExpirationDividerLabels((prev) => {
        if (prev.size === nextHiddenLabels.size && [...prev].every((label) => nextHiddenLabels.has(label))) {
          return prev;
        }
        return nextHiddenLabels;
      });
    };

    updatePinnedDividerRef.current = updatePinnedDivider;
    updatePinnedDivider();
    requestAnimationFrame(updatePinnedDivider);

    container.addEventListener('scroll', updatePinnedDivider, { passive: true });
    window.addEventListener('resize', updatePinnedDivider);

    const resizeObserver = new ResizeObserver(updatePinnedDivider);
    resizeObserver.observe(container);
    const thead = planTableHeadRef.current;
    if (thead) {
      resizeObserver.observe(thead);
    }

    return () => {
      updatePinnedDividerRef.current = null;
      container.removeEventListener('scroll', updatePinnedDivider);
      window.removeEventListener('resize', updatePinnedDivider);
      resizeObserver.disconnect();
    };
  }, [showExpirationSections, loading, dividends?.length]);

  useLayoutEffect(() => {
    if (!showExpirationSections) {
      return;
    }
    updatePinnedDividerRef.current?.();
  });

  // Функция для воспроизведения звука алерта
  const playAlertSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800; // Частота звука (Гц)
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.error('Ошибка воспроизведения звука:', error);
    }
  };

  // Обработка сработавших алертов текущего пользователя (приходят персонально по SSE)
  useEffect(() => {
    if (isPromo || !userId || !Array.isArray(triggeredAlerts) || triggeredAlerts.length === 0) {
      return;
    }

    const userAlerts = triggeredAlerts;

    // Создаем уникальный ключ для этого набора алертов
    const alertsKey = userAlerts.map(a => `${a.stock}.${a.futures}.${a.expPercent}.${a.divPercent}.${a.spread}`).sort().join('|');
    
    // Проверяем, обрабатывали ли мы уже эти алерты
    if (processedAlertsRef.current.has(alertsKey)) {
      return;
    }

    // Помечаем эти алерты как обработанные
    processedAlertsRef.current.add(alertsKey);

    console.log('Сработавшие алерты для пользователя', userId, ':', userAlerts);

    // Воспроизводим звук при получении алертов (только один раз)
    playAlertSound();

    const newHighlightedRows = new Set();

    // Подсвечиваем строки, соответствующие сработавшим алертам
    userAlerts.forEach(alert => {
      // Находим строку по stock и futures
      const rowId = `${alert.stock}.${alert.futures}`;
      console.log('Подсвечиваем строку:', rowId);
      newHighlightedRows.add(rowId);
      
      // Если алерт пришел для выделенной строки, снимаем обычное выделение
      // Используем ref, чтобы не добавлять selectedRowId в зависимости useEffect
      if (selectedRowIdRef.current === rowId) {
        setSelectedRowId(null);
      }
    });

    if (newHighlightedRows.size > 0) {
      console.log('Подсвеченные строки:', Array.from(newHighlightedRows));
      setHighlightedRows(newHighlightedRows);

      // Убираем подсветку через 15 секунд
      const timeoutId = setTimeout(() => {
        setHighlightedRows(new Set());
      }, 15000);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [triggeredAlerts, userId, isPromo]);

  // Все хуки должны быть вызваны до любых условных возвратов
  const broker = useMemo(() => (selectedBroker ?? 'bcs').toLowerCase(), [selectedBroker]); // 'bcs' или 'finam'
  const normalizedSearchTerms = useMemo(
    () => parseSearchTerms(searchQuery),
    [searchQuery]
  );

  const sortedDividends = useMemo(() => {
    if (!dividends || dividends.length === 0) return [];

    return [...dividends].sort((a, b) => {
      if (showExpirationSections) {
        const aExpiration = getPlanExpirationSortValue(a);
        const bExpiration = getPlanExpirationSortValue(b);
        if (aExpiration < bExpiration) return -1;
        if (aExpiration > bExpiration) return 1;
      }

      if (sortConfig.key) {
        const columnCompare = comparePlanItemsByColumn(a, b, sortConfig, broker);
        if (columnCompare !== 0) {
          return columnCompare;
        }
      } else if (!showExpirationSections) {
        return 0;
      }

      return String(a.stock).localeCompare(String(b.stock), 'ru', { sensitivity: 'base' });
    });
  }, [dividends, sortConfig, broker, showExpirationSections]);

  const displayedDividends = useMemo(() => {
    if (isPromo || !showApprovedDividendsOnly) return sortedDividends;
    return sortedDividends.filter(item => item.dividend?.approved);
  }, [sortedDividends, showApprovedDividendsOnly, isPromo]);

  const SortableHeader = ({ children, sortKey, onClick }) => (
    <th 
      onClick={() => onClick(sortKey)}
      className={`sortable ${sortConfig.key === sortKey ? 'active' : ''}`}
      title="Кликните для сортировки"
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
        {children}
      </div>
    </th>
  );

  const closeMarginTooltip = useCallback(() => {
    setHoveredMarginItemId(null);
  }, []);

  const closeMobileOrderPanel = useCallback(() => {
    setIsMobileOrderPanelOpen(false);
  }, []);

  const clearPlanRowLongPress = useCallback(() => {
    const pressState = planRowLongPressRef.current;
    if (pressState.timer) {
      clearTimeout(pressState.timer);
      pressState.timer = null;
    }
  }, []);

  const handlePlanRowTouchStart = useCallback((rowId) => {
    if (!isPlanMobile || isPromo) return;

    const pressState = planRowLongPressRef.current;
    pressState.triggered = false;
    clearPlanRowLongPress();
    pressState.timer = window.setTimeout(() => {
      pressState.timer = null;
      pressState.triggered = true;
      clearTextSelection();
      setSelectedRowId(rowId);
      setIsMobileOrderPanelOpen(true);
      closeMarginTooltip();
      hideSpreadHistoryTooltip();
    }, 450);
  }, [isPlanMobile, isPromo, clearPlanRowLongPress, closeMarginTooltip, hideSpreadHistoryTooltip]);

  const handlePlanRowTouchEnd = useCallback(() => {
    clearPlanRowLongPress();
  }, [clearPlanRowLongPress]);

  const handlePlanRowTouchMove = useCallback(() => {
    clearPlanRowLongPress();
  }, [clearPlanRowLongPress]);

  const openDesktopMarginTooltip = useCallback((item, event) => {
    if (!item[broker]?.margin?.length) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 450;
    const tooltipHeight = 420;
    let x = rect.right + 10;
    let y = rect.top;

    if (x + tooltipWidth > window.innerWidth) {
      x = rect.left - tooltipWidth - 10;
      if (x < 0) {
        x = window.innerWidth - tooltipWidth - 10;
      }
    }

    if (y + tooltipHeight > window.innerHeight) {
      y = window.innerHeight - tooltipHeight - 10;
      if (y < 0) {
        y = rect.top - tooltipHeight - 10;
        if (y < 0) {
          y = 10;
        }
      }
    }

    if (y < 0) {
      y = 10;
    }

    setTooltipPosition({ x, y });
    setHoveredMarginItemId(item.id);
  }, [broker]);

  const handleMarginCellClick = useCallback((event, item) => {
    if (!item[broker]?.margin?.length) {
      return;
    }
    event.stopPropagation();
    hideSpreadHistoryTooltip();
    setHoveredMarginItemId((current) => (current === item.id ? null : item.id));
  }, [broker, hideSpreadHistoryTooltip]);

  const handleSpreadCellClick = useCallback(async (event, item) => {
    const cacheKey = getSpreadHistoryKey(item);
    if (!cacheKey) {
      return;
    }
    event.stopPropagation();
    closeMarginTooltip();

    if (spreadHistoryHoverKeyRef.current === cacheKey) {
      hideSpreadHistoryTooltip();
      return;
    }

    await loadSpreadHistoryForItem(item);
  }, [closeMarginTooltip, hideSpreadHistoryTooltip, loadSpreadHistoryForItem]);

  if (loading) {
    return (
      <div className="App">
        <div className="container">
          <div className="loading">
            Загрузка данных ...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="container">
          <div className="error">
            <p>Ошибка при загрузке данных: {error}</p>
            <button className="retry-button" onClick={() => window.location.reload()}>
              Попробовать снова
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dividends || dividends.length === 0) {
    return (
      <div className="App">
        <div className="container">
          <div className="no-data">
            Нет данных для отображения
          </div>
        </div>
      </div>
    );
  }

  // Получаем информацию о выделенной строке для алерта
  const getSelectedRowInfoForAlert = () => {
    if (!selectedRowId) return null;
    const item = sortedDividends.find(item => selectedRowId === item.id);
    if (!item) return null;
    const calculatedFields = item[broker]?.calculatedFields || {};
    return {
      stock: item.stock,
      futures: item.futures.ticker,
      stockPrice: item[broker]?.stock?.ask?.price || null,
      futuresPrice: item[broker]?.futures?.ask?.price || null,
      maxProfit: calculatedFields.maxProfit || null,
      minProfit: calculatedFields.minProfit || null,
      inputSpread: calculatedFields.inputSpread || null,
    };
  };

  const handleSubmitOrder = async (broker, summa, count) => {
    if (!selectedRowId) return;
    
    const selectedItem = sortedDividends.find(item => selectedRowId === item.id);
    if (!selectedItem) {
      alert('Не удалось найти выбранную строку');
      return;
    }
    
    if (!count || count <= 0) {
      alert('Некорректное количество фьючерсов');
      return;
    }
    
    try {
      const orderData = {
        broker: broker,
        stock: selectedItem.stock,
        futures: selectedItem.futures.ticker,
        futuresCount: count
      };
      
      const response = await authService.authenticatedRequest(SERVER_URL + '/v2/plan/place-order', {
        method: 'POST',
        body: JSON.stringify(orderData)
      });
      
      const result = await response.json();
      if (!result.ok) {
        throw new Error(result.error || 'Не удалось создать заказ');
      }
      
      setSelectedRowId(null);
      setIsMobileOrderPanelOpen(false);
      
    } catch (error) {
      showToast(`Ошибка создания заказа: ${error.message}`, 'error');
    }
  };

  const handleAlertButtonClick = () => {
    const selectedRow = getSelectedRowInfoForAlert();
    if (!selectedRow) {
      alert('Выберите строку для создания алерта');
      return;
    }
    setIsMobileOrderPanelOpen(false);
    setAlertInitialValues({
      stock: selectedRow.stock,
      futures: selectedRow.futures,
      stockPrice: selectedRow.stockPrice,
      futuresPrice: selectedRow.futuresPrice,
      maxProfit: selectedRow.maxProfit,
      minProfit: selectedRow.minProfit,
      inputSpread: selectedRow.inputSpread,
    });
    setIsAlertModalOpen(true);
  };

  const handleWriteOrderClick = () => {
    const selectedItem = sortedDividends.find(item => selectedRowId === item.id);
    if (!selectedItem) {
      alert('Выберите строку для записи сделки');
      return;
    }
    setIsMobileOrderPanelOpen(false);
    setIsWriteOrderModalOpen(true);
  };

  const handleSubmitWriteOrder = async ({ date, count, stockPrice, futuresPrice }) => {
    const selectedItem = sortedDividends.find(item => selectedRowId === item.id);
    if (!selectedItem) {
      showToast('Не удалось найти выбранную строку', 'error');
      return;
    }
    try {
      const writeOrderData = {
        broker: broker,
        stock: selectedItem.stock,
        futures: selectedItem.futures.ticker,
        futuresCount: count,
        dayOpen: date,
        stockPriceOpen: stockPrice,
        futuresPriceOpen: futuresPrice
      };
      const response = await authService.authenticatedRequest(SERVER_URL + '/v2/plan/write-order', {
        method: 'POST',
        body: JSON.stringify(writeOrderData)
      });
      const result = await response.json();
      if (!result.ok) {
        throw new Error(result.error || 'Не удалось записать сделку');
      }
      showToast('Сделка записана', 'success');
      setSelectedRowId(null);
      setIsMobileOrderPanelOpen(false);
      setIsWriteOrderModalOpen(false);
    } catch (error) {
      showToast(`Ошибка записи: ${error.message}`, 'error');
    }
  };

  const handleAutofillButtonClick = (amount, percentDo, percentExp, spread) => {
    setIsMobileOrderPanelOpen(false);
    setAutofillInitialValues({
      sum: amount,
      percentDo: percentDo != null && percentDo !== undefined ? percentDo : null,
      percentExp: percentExp != null && percentExp !== undefined ? percentExp : null,
      spread: spread != null && spread !== undefined ? spread : null
    });
    setIsAutofillModalOpen(true);
  };

  const handleCreateAutofillCommand = async ({ sum, percentDo, percentExp, spread }) => {
    const selectedRow = getSelectedRowInfoForAlert();
    if (!selectedRow) {
      showToast('Не удалось найти выбранную строку', 'error');
      return;
    }
    const selectedItem = sortedDividends.find(item => selectedRowId === item.id);
    if (!selectedItem) {
      showToast('Не удалось найти выбранную строку', 'error');
      return;
    }

    try {
      const side = 'enter';
      const body = {
        broker: selectedBroker,
        stock: selectedRow.stock,
        futures: selectedRow.futures,
        summNeed: sum,
        lotSize: selectedItem.futures.lotSize,
        percentDO: percentDo ?? null,
        percentExp: percentExp ?? null,
        spread: spread ?? null,
        side
      };
      await authService.authenticatedRequest(SERVER_URL + '/v1/robot/command', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      showToast('Команда автонабора создана', 'success');
      setIsAutofillModalOpen(false);
    } catch (error) {
      showToast(`Ошибка создания команды: ${error.message}`, 'error');
    }
  };

  const handleCreateAlert = async (divPercent, expPercent, spread, stockPrice, futuresPrice, broker) => {
    const selectedRow = getSelectedRowInfoForAlert();
    if (!selectedRow) {
      showToast('Не удалось найти выбранную строку', 'error');
      return;
    }

    try {
      await authService.authenticatedRequest(SERVER_URL + '/v1/alerts', {
        method: 'POST',
        body: JSON.stringify({
          stock: selectedRow.stock,
          futures: selectedRow.futures,
          stockPrice: stockPrice ?? null,
          futuresPrice: futuresPrice ?? null,
          divPercent: divPercent || null,
          expPercent: expPercent || null,
          spread: spread ?? null,
          broker: broker
        })
      });

      setIsAlertModalOpen(false);
      showToast('Алерт успешно создан', 'success');
    } catch (error) {
      showToast(`Ошибка создания алерта: ${error.message}`, 'error');
    }
  };

  return (
    <div className="App">
      <div className={`plan-page${isPromo ? ' plan-page--promo' : ''}`}>
      <div className={`container${isPromo ? ' plan-page-container--stacked' : ' plan-page-container--classic'}`}>
        <div className={`plan-page-toolbar${isPromo ? '' : ' plan-page-toolbar--overlay'}`}>
          <label htmlFor="broker-select" className="plan-page-toolbar-label">
            Брокер:
          </label>
          <select
            id="broker-select"
            className="plan-page-toolbar-select"
            value={selectedBroker ?? 'bcs'}
            onChange={(e) => handleBrokerChange(e.target.value)}
            disabled
            aria-label="Брокер"
          >
            <option value="bcs">БКС</option>
            <option value="finam">Финам</option>
          </select>
          <input
            className="plan-search-input"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            title="Поиск по акции и фьючу. Несколько значений через запятую, например: SBER, GAZP"
            aria-label="Поиск по акции и фьючу, несколько значений через запятую"
          />
          <label
            className="approved-dividends-toggle"
            title="Разделить таблицу по срокам экспирации фьючерса"
          >
            <input
              type="checkbox"
              checked={showExpirationSections}
              onChange={(e) => setShowExpirationSections(e.target.checked)}
              aria-label="Разделить таблицу по срокам экспирации фьючерса"
            />
            <span className="approved-dividends-toggle__slider" aria-hidden="true"></span>
          </label>
          {!isPromo && (
          <>
          <label
            className="approved-dividends-toggle"
            title="Только с рекомендованными и утвержденными дивидендами"
          >
            <input
              type="checkbox"
              checked={showApprovedDividendsOnly}
              onChange={(e) => setShowApprovedDividendsOnly(e.target.checked)}
              aria-label="Только с рекомендованными и утвержденными дивидендами"
            />
            <span className="approved-dividends-toggle__slider" aria-hidden="true"></span>
          </label>
          <label
            className="approved-dividends-toggle"
            title="Показывать спред выхода"
          >
            <input
              type="checkbox"
              checked={showExitSpread}
              onChange={(e) => setShowExitSpread(e.target.checked)}
              aria-label="Показывать спред выхода"
            />
            <span className="approved-dividends-toggle__slider" aria-hidden="true"></span>
          </label>
          <label
            className="approved-dividends-toggle"
            title="Показывать доходность без дивидендов"
          >
            <input
              type="checkbox"
              checked={showProfitWithoutDividends}
              onChange={(e) => setShowProfitWithoutDividends(e.target.checked)}
              aria-label="Показывать доходность без дивидендов"
            />
            <span className="approved-dividends-toggle__slider" aria-hidden="true"></span>
          </label>
          </>
          )}
          <label
            className="approved-dividends-toggle plan-page-toolbar-toggle--desktop"
            title="Показывать логотипы акций"
          >
            <input
              type="checkbox"
              checked={showStockLogos}
              onChange={(e) => setShowStockLogos(e.target.checked)}
              aria-label="Показывать логотипы акций"
            />
            <span className="approved-dividends-toggle__slider" aria-hidden="true"></span>
          </label>
          {!isPromo && (
          <label
            className="approved-dividends-toggle plan-page-toolbar-toggle--mobile"
            title="Показывать столбцы Фьюч и Акция Фьюч"
          >
            <input
              type="checkbox"
              checked={showFuturesColumns}
              onChange={(e) => setShowFuturesColumns(e.target.checked)}
              aria-label="Показывать столбцы Фьюч и Акция Фьюч"
            />
            <span className="approved-dividends-toggle__slider" aria-hidden="true"></span>
          </label>
          )}
        </div>
        
        {/* Информация о КС и спредах справа */}
        {!isPromo && metadata.fairSpreads && Array.isArray(metadata.fairSpreads) && metadata.fairSpreads.length > 0 && (
          <div className="fair-spread-info">
            <div className="fair-spread-info__desktop">
              <div style={{ display: 'flex', gap: '40px', justifyContent: 'flex-end' }}>
                <div className="fair-spread-info__col">
                  Текущая КС: {formatNumber(metadata.keyrate || 0, 2)}% <br/>
                  Справедливые спреды:
                </div>
                <div className="fair-spread-info__col">
                  {metadata.fairSpreads.map((item, index) => (
                    <div key={index}>
                      {index === 0 ? 'Ближайшая' : 'Следующая'} экспирация {item.day}.{String(item.month).padStart(2, '0')}: [ {formatNumber(item.value * 0.9, 1)} - {formatNumber(item.value, 1)} ] %
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="fair-spread-info__mobile">
              КС {formatNumber(metadata.keyrate || 0, 2)}%,
              Спреды: {metadata.fairSpreads.map((item, index) => (
                <span key={index}>
                  {`${index !== 0 ? ',' : ''} ${item.day}.${String(item.month).padStart(2, '0')}: [${formatNumber(item.value * 0.9, 1)} - ${formatNumber(item.value, 1)}]%`}
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="plan-table-scroll-wrapper">
          {showExpirationSections && pinnedExpirationDividerText && (
            <div
              className="expiration-section-divider-pinned"
              style={{ top: `${planTableHeadHeight}px` }}
            >
              {pinnedExpirationDividerText}
            </div>
          )}
          <div ref={planTableContainerRef} className="table-container">
          <table
            className={`dividend-table${showExpirationSections ? ' dividend-table--expiration-sections' : ''}`}
          >
            <thead ref={planTableHeadRef}>
              <tr>
                <SortableHeader sortKey="stock" onClick={handleSort}>Акция</SortableHeader>
                {arePlanFuturesColumnsVisible(isPromo, isPlanMobile, showFuturesColumns) ? (
                  <SortableHeader sortKey="expirationDaysLeft" onClick={handleSort}>Фьюч</SortableHeader>
                ) : null}
                {!isPromo && (
                  <>
                    <SortableHeader sortKey="dividendBorderLeft" onClick={handleSort}>Дата ДО</SortableHeader>
                    <SortableHeader sortKey="dividendYield" onClick={handleSort}>Дивы</SortableHeader>
                  </>
                )}
                {isPromo ? (
                  <>
                    <SortableHeader sortKey="stockPrice" onClick={handleSort}>Цена А</SortableHeader>
                    <SortableHeader sortKey="futuresPrice" onClick={handleSort}>Цена Ф</SortableHeader>
                  </>
                ) : arePlanFuturesColumnsVisible(isPromo, isPlanMobile, showFuturesColumns) ? (
                  <SortableHeader sortKey="stockPrice" onClick={handleSort}>Акция Фьюч</SortableHeader>
                ) : null}
                {/* <SortableHeader sortKey="futuresPrice" onClick={handleSort}>Цена фьюча</SortableHeader> */}
                <SortableHeader sortKey="inputSpread" onClick={handleSort}>Спред %</SortableHeader>
                {/* <SortableHeader sortKey="fairPrice" onClick={handleSort}>Спр цена</SortableHeader> */}
                {!isPromo && <SortableHeader sortKey="delta" onClick={handleSort}>Дельта %</SortableHeader>}
                {/* <SortableHeader sortKey="allPercents" onClick={handleSort}>Всего %</SortableHeader> */}
                {/* <SortableHeader sortKey="expirationDaysLeft" onClick={handleSort}>До эксп.</SortableHeader> */}
                <SortableHeader sortKey="maxProfit" onClick={handleSort}>{isPromo ? 'Макс. %%' : '%% к ДО'}</SortableHeader>
                <SortableHeader sortKey="minProfit" onClick={handleSort}>{isPromo ? 'Мин. %%' : '%% к эксп.'}</SortableHeader>
                {!isPromo && <SortableHeader sortKey="volume" onClick={handleSort}>Объем (5x)</SortableHeader>}
              </tr>
            </thead>
            <tbody>
              {displayedDividends.length === 0 && (
                <tr>
                  <td colSpan={getPlanTableColumnCount(isPromo, isPlanMobile, showFuturesColumns)} className="plan-empty-filter">
                    {isPromo ? 'Нет данных для отображения' : 'Нет активов с рекомендованными и утвержденными дивидендами'}
                  </td>
                </tr>
              )}
              {displayedDividends.map((item, index) => {
                const isError = item.error;
                const calculatedFields = item[broker]?.calculatedFields || {};
                const futures = item.futures || {};
                const stockPrice = item[broker]?.stock?.ask?.price;
                const rowId = item.id;
                const dividendBorderLeft = Number(futures.dividendBorderLeft);
                const isDividendDateNear = isDefined(futures.dividendBorderLeft) && !Number.isNaN(dividendBorderLeft) && dividendBorderLeft <= 15;
                const isSearchMatch = rowMatchesSearchTerms(
                  item.stock,
                  item.futures?.ticker,
                  normalizedSearchTerms
                );
                const canLoadSpreadHistory = Boolean(item.stock && item.futures?.ticker);
                const stockLink = (item.dividend?.url || item.link || '').trim();
                const expirationKey = getPlanExpirationKey(item);
                const previousExpirationKey = index > 0
                  ? getPlanExpirationKey(displayedDividends[index - 1])
                  : null;
                const showExpirationDivider = showExpirationSections
                  && (index === 0 || expirationKey !== previousExpirationKey);
                const fairSpread = isPromo ? null : getFairSpreadForExpiration(futures.expirationDate, metadata.fairSpreads);
                const expirationDividerText = formatPlanExpirationDividerText(
                  futures.expirationDate,
                  futures.expirationDaysLeft,
                  fairSpread,
                  isPlanMobile
                );
                return (
                  <React.Fragment key={rowId}>
                  {showExpirationDivider && (
                    <tr
                      className={
                        'expiration-section-divider'
                        + (hiddenExpirationDividerLabels.has(expirationDividerText)
                          ? ' expiration-section-divider--overlapped'
                          : '')
                      }
                      data-section-label={expirationDividerText}
                    >
                      <td colSpan={getPlanTableColumnCount(isPromo, isPlanMobile, showFuturesColumns)}>
                        {expirationDividerText}
                      </td>
                    </tr>
                  )}
                  <tr
                    data-plan-row-id={rowId}
                    className={
                      (isError ? 'error-row ' : '') + 
                      (isSearchMatch ? 'search-highlight-row ' : '') +
                      (selectedRowId === rowId ? 'selected-row' : '') +
                      (highlightedRows.has(rowId) ? ' alert-triggered-row' : '') +
                      (isPlanMobile ? ' plan-row--mobile-selectable' : '')
                    }
                    style={{ cursor: 'pointer' }}
                    onSelectStart={preventModalOverlaySelection}
                    onTouchStart={isPlanMobile ? () => handlePlanRowTouchStart(rowId) : undefined}
                    onTouchEnd={isPlanMobile ? handlePlanRowTouchEnd : undefined}
                    onTouchMove={isPlanMobile ? handlePlanRowTouchMove : undefined}
                    onTouchCancel={isPlanMobile ? handlePlanRowTouchEnd : undefined}
                    onContextMenu={isPlanMobile ? (event) => event.preventDefault() : undefined}
                    onClick={() => {
                      clearTextSelection();
                      if (isPlanMobile && planRowLongPressRef.current.triggered) {
                        planRowLongPressRef.current.triggered = false;
                        return;
                      }

                      // Если строка подсвечена алертом, убираем подсветку
                      if (highlightedRows.has(rowId)) {
                        setHighlightedRows(prev => {
                          const newSet = new Set(prev);
                          newSet.delete(rowId);
                          return newSet;
                        });
                      }
                      
                      // Если строка уже выделена, снимаем выделение, иначе выделяем её
                      if (selectedRowId === rowId) {
                        setSelectedRowId(null);
                        if (isPlanMobile) {
                          setIsMobileOrderPanelOpen(false);
                        }
                      } else {
                        setSelectedRowId(rowId);
                      }
                    }}
                  >
                    <td>
                      <div className="plan-stock-cell">
                        {showStockLogos && !isPlanMobile && <StockLogo src={item.stockLogoLink} alt={item.stock} />}
                        {isPromo ? (
                          item.stock
                        ) : stockLink ? (
                          <a href={stockLink} target="_blank" rel="noopener noreferrer" className="company-link">
                            {item.stock}
                          </a>
                        ) : (
                          item.stock
                        )}
                      </div>
                    </td>
                    {arePlanFuturesColumnsVisible(isPromo, isPlanMobile, showFuturesColumns) && (
                    <td>
                      {isPromo
                        ? item.futures.ticker
                        : (
                          <>
                            {futures.expirationDaysLeft <= 3 ? <span className="orange-text">{formatNumber(futures.expirationDaysLeft, 0)}</span> : formatNumber(futures.expirationDaysLeft, 0)}<br/>
                            {item.futures.ticker}
                          </>
                        )}
                    </td>
                    )}
                    {!isPromo && (
                    <>
                    <td>{
                      item.dividend
                      ? <>
                        {isDividendDateNear ? <span className="warning-color">{formatNumber(futures.dividendBorderLeft, 0)}</span> : <span>{formatNumber(futures.dividendBorderLeft, 0)}</span>}<br/>
                        {isDividendDateNear ? <span className="warning-color">{formatDate(item.dividend?.date)}</span> : formatDate(item.dividend?.date)}
                        </> 
                      : NULL_DATA }
                    </td>
                    {/* Дивы */}
                    <td>
                      {item.dividend
                        ? item.dividend?.approved 
                          ? <span className="green-text"> <span className="bold-text">{ isDefined(calculatedFields.dividendYield) ? formatPercent(calculatedFields.dividendYield) : NO_DATA}</span> <br/> {formatNumber(item.dividend.size)}</span>
                          : <span>                        <span className="bold-text">{ isDefined(calculatedFields.dividendYield) ? formatPercent(calculatedFields.dividendYield) : NO_DATA}</span> <br/> {formatNumber(item.dividend.size)}</span>
                        : NULL_DATA
                      }
                    </td>
                    </>
                    )}
                    {/* Акция Фьюч / Цена А + Цена Ф */}
                    {isPromo ? (
                      <>
                        <td>
                          {isDefined(stockPrice)
                            ? <span className={getQuotePriceClass(item[broker]?.stock)}>{formatNumber(stockPrice)}</span>
                            : NO_DATA}
                        </td>
                        <td>
                          {isDefined(item[broker]?.futures?.bid?.price)
                            ? <span className={getQuotePriceClass(item[broker]?.futures)}>{formatNumber(item[broker]?.futures?.bid?.price)}</span>
                            : NO_DATA}
                        </td>
                      </>
                    ) : arePlanFuturesColumnsVisible(isPromo, isPlanMobile, showFuturesColumns) ? (
                    <td>
                      {isDefined(stockPrice)
                        ? <span className={getQuotePriceClass(item[broker]?.stock)}>{formatNumber(stockPrice)}</span>
                        : NO_DATA}
                      <br/>
                      {isDefined(item[broker]?.futures?.bid?.price) && futures.lotSize
                        ? <span className={getQuotePriceClass(item[broker]?.futures)}>({futures.lotSize}). {formatNumber(item[broker]?.futures?.bid?.price / futures.lotSize)}</span>
                        : NO_DATA}
                    </td>
                    ) : null}
                    {/* Спред % */}
                    <td
                      style={{
                        cursor: canLoadSpreadHistory ? (isPlanMobile ? 'pointer' : 'help') : 'default'
                      }}
                      onClick={canLoadSpreadHistory && isPlanMobile ? (event) => handleSpreadCellClick(event, item) : undefined}
                      onMouseEnter={canLoadSpreadHistory && !isPlanMobile ? (event) => openDesktopSpreadHistoryTooltip(event, item) : undefined}
                      onMouseLeave={canLoadSpreadHistory && !isPlanMobile ? hideSpreadHistoryTooltip : undefined}
                    >
                      {!isPromo && showExitSpread
                        ? (
                          <div className="profit-split-cell">
                            <span className="spread-text">{isDefined(calculatedFields.inputSpread) ? formatPercent(calculatedFields.inputSpread, 2) : NO_DATA}</span>
                            <span className="spread-text">({isDefined(calculatedFields.exitSpread) ? formatPercent(calculatedFields.exitSpread, 2) : NO_DATA})</span>
                          </div>
                        )
                        : isDefined(calculatedFields.inputSpread)
                          ? <span className="spread-text">{formatPercent(calculatedFields.inputSpread, 2)}</span>
                          : NO_DATA}
                    </td>
                    {/* <td>{formatNumber(brokerCalculatedFields.fairPrice)}</td> */}
                    {/* Дельта % */}
                    {!isPromo && (
                    <td>{isDefined(calculatedFields.delta) ? <span className="delta-text">{formatPercent(calculatedFields.delta)}</span> : NO_DATA}</td>
                    )}
                    {/* <td>{formatPercent(calculatedFields.allPercents)}</td> */}
                    {/* %% к ДО */}
                    <td
                      style={{ position: 'relative', cursor: !isPromo && item[broker]?.margin && Array.isArray(item[broker].margin) && item[broker].margin.length > 0 ? (isPlanMobile ? 'pointer' : 'help') : 'default' }}
                      onClick={!isPromo && isPlanMobile ? (event) => handleMarginCellClick(event, item) : undefined}
                      onMouseEnter={!isPromo && !isPlanMobile ? (event) => openDesktopMarginTooltip(item, event) : undefined}
                      onMouseLeave={!isPromo && !isPlanMobile ? closeMarginTooltip : undefined}
                    >
                      {isDefined(calculatedFields.maxProfit) 
                        ? formatPercent(calculatedFields.maxProfit) 
                        : !isPromo && item.dividend
                          ? NO_DATA
                          : NULL_DATA}
                    </td>
                    <td>
                      {!isPromo && showProfitWithoutDividends
                        ? (
                          <div className="profit-split-cell">
                            {item.dividend
                              ? <span>{isDefined(calculatedFields.minProfit) ? formatPercent(calculatedFields.minProfit) : NO_DATA}</span>
                              : <span>{}</span>}
                            <span>({isDefined(calculatedFields.minProfitWithoutDivs) ? formatPercent(calculatedFields.minProfitWithoutDivs) : NO_DATA})</span>
                          </div>
                        )
                        : isDefined(calculatedFields.minProfit) ? formatPercent(calculatedFields.minProfit) : NO_DATA}
                    </td>
                    {!isPromo && (
                    <td>{
                      isDefined(item[broker]?.futures?.bid?.priceSeveral)
                      ? <>
                        <span className="green-text">{formatNumber((item[broker]?.stock?.ask?.priceSeveral || 0) * (item[broker]?.futures?.bid?.sizeSeveral || 0) * (futures.lotSize || 0), 0)}</span><br/>
                        <span className="spread-text">({formatNumber(-((item[broker]?.futures?.bid?.priceSeveral || 0) - (item[broker]?.futures?.bid?.price || 0)) / (item[broker]?.futures?.bid?.price || 1) * 100, 1)}%) {item[broker]?.futures?.bid?.sizeSeveral || 0}</span>
                        </> 
                      : NO_DATA }
                    </td>
                    )}
                  </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Таблица команд робота под основной таблицей, как на листе сделок */}
      {!isPromo && !isPlanMobile && <RobotCommandsTable variant="embedded" />}
      </div>

      {/* Tooltip с таблицей margin при наведении на ячейку "%% к ДО" */}
      {!isPromo && isPlanMobile && hoveredMarginItemId && (
        <button
          type="button"
          className="margin-tooltip-backdrop"
          aria-label="Закрыть маржинальные расчёты"
          onClick={closeMarginTooltip}
          onSelectStart={preventModalOverlaySelection}
        />
      )}
      {!isPromo && hoveredMarginItemId && (() => {
        const hoveredItem = sortedDividends.find((item) => item.id === hoveredMarginItemId);
        if (!hoveredItem || !hoveredItem[broker]?.margin || !Array.isArray(hoveredItem[broker].margin)) {
          return null;
        }
        const marginData = hoveredItem[broker].margin;
        const modelPrices = isPlanMobile
          ? ['-20%', 'Гэп', 'Б/гэпа', '+20%']
          : ['-20%', 'Див.гэп', 'Без гэпа', '+20%'];
        const marginRows = [
          { label: 'Фин рез %', tone: 'profit', getValue: (item) => item.finResPercent },
          { label: 'Фин рез % год', tone: 'profit', getValue: (item) => item.finResPercentYear },
          { label: 'Ч Фин рез %', tone: 'cyan', getValue: (item) => item.clearFinResPercent },
          { label: 'Ч фин рез % год', tone: 'cyan', getValue: (item) => item.clearFinResPercentYear },
          { label: 'Снижение НБ %', tone: 'warning', getValue: (item) => item.taxBaseDecrease },
          { label: 'x2 фин рез %', tone: 'warning', getValue: (item) => item.x2FinResPercent },
          { label: 'x2 фин рез % год', tone: 'warning', getValue: (item) => item.x2FinResPercentYear },
          { label: 'x2 доха с НДФЛ %', tone: 'accent', getValue: (item) => item.x2ProfitabilityWithNDFL },
          { label: 'x2 доха с НДФЛ % год', tone: 'accent', getValue: (item) => item.x2ProfitabilityWithNDFLYear },
          { label: 'x2 Снижение НБ %', tone: 'warning', getValue: (item) => item.x2TaxBaseDecrease },
        ];
        
        return (
          <div
            className={`margin-tooltip${isPlanMobile ? ' margin-tooltip--mobile' : ''}`}
            style={isPlanMobile ? undefined : {
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
            }}
            onSelectStart={isPlanMobile ? preventModalOverlaySelection : undefined}
          >
            <div className="margin-tooltip__title">
              {hoveredItem.futures.ticker} с маржой ({selectedBroker === 'bcs' ? 'БКС' : 'Финам'})
            </div>
            <div className="margin-tooltip__scroll">
              <table className="margin-tooltip__table">
                <thead>
                  <tr>
                    <th></th>
                    {modelPrices.map((price) => (
                      <th key={price}>{price}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {marginRows.map((row) => (
                    <tr key={row.label} className={`margin-tooltip__row margin-tooltip__row--${row.tone}`}>
                      <td className="margin-tooltip__label">{row.label}</td>
                      {marginData.map((marginItem, idx) => (
                        <td key={idx} className="margin-tooltip__value">
                          {formatPercent(row.getValue(marginItem))}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Tooltip с графиком истории спреда при наведении на ячейку "Спред %" */}
      {isPlanMobile && hoveredSpreadItem && (
        <button
          type="button"
          className="margin-tooltip-backdrop"
          aria-label="Закрыть историю спреда"
          onClick={hideSpreadHistoryTooltip}
          onSelectStart={preventModalOverlaySelection}
        />
      )}
      {hoveredSpreadItem && (() => {
        const hoveredItem = hoveredSpreadItem;
        const spreadHistory = hoveredSpreadHistory.data;

        return (
          <div
            className={`spread-history-tooltip${isPlanMobile ? ' spread-history-tooltip--mobile' : ''}`}
            style={isPlanMobile ? undefined : {
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
            }}
            onSelectStart={isPlanMobile ? preventModalOverlaySelection : undefined}
          >
            {isPlanMobile && (
              <div className="spread-history-tooltip__title">
                {hoveredItem.stock} / {hoveredItem.futures.ticker} — история спреда
              </div>
            )}
            <div className="spread-history-tooltip__body">
              {hoveredSpreadHistory.loading ? (
                <div className="spread-history-tooltip__loading">Загрузка истории спреда...</div>
              ) : hoveredSpreadHistory.error ? (
                <div className="spread-history-tooltip__error">Ошибка получения сведений.<br/>Попробуйте еще раз.</div>
              ) : hoveredSpreadHistory.unavailable ? (
                <div className="spread-history-tooltip__empty">Нет данных для отображения</div>
              ) : (
                <SpreadHistoryChart
                  history={spreadHistory}
                  currentValue={hoveredItem?.[broker]?.calculatedFields?.inputSpread}
                  compact={isPlanMobile}
                />
              )}
            </div>
          </div>
        );
      })()}

      {/* Боковая панель создания заказа - вне контейнера */}
      {!isPromo && !isPlanMobile && (() => {
        const selectedItem = selectedRowId
          ? sortedDividends.find(item => selectedRowId === item.id)
          : null;
        const isRowSelected = Boolean(selectedItem);

        return (
          <>
            <div style={{
              position: 'fixed',
              left: SIDE_PANEL_LEFT,
              bottom: SIDE_PANEL_BOTTOM,
              width: SIDE_PANEL_WIDTH,
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              zIndex: 1000,
            }}>
              <MarginIndicatorsPanel marginIndicators={marginIndicators} />
              <CreateOrderPanel
              stock={selectedItem?.stock || '-'}
              futures={selectedItem?.futures?.ticker || '-'}
              stockPrice={selectedItem?.[broker]?.stock?.ask?.price || null}
              futuresPrice={selectedItem?.[broker]?.futures?.ask?.price || null}
              stockIsTrading={selectedItem?.[broker]?.stock?.isTrading}
              futuresIsTrading={selectedItem?.[broker]?.futures?.isTrading}
              lotSize={selectedItem?.futures?.lotSize || null}
              broker={selectedBroker}
              onSubmit={handleSubmitOrder}
              percentDo={selectedItem?.[broker]?.calculatedFields?.maxProfit || null}
              percentExp={selectedItem?.[broker]?.calculatedFields?.minProfit || null}
              spread={selectedItem?.[broker]?.calculatedFields?.inputSpread || null}
              onAutofillClick={handleAutofillButtonClick}
              isRowSelected={isRowSelected}
            />
            </div>
            {/* Круглая кнопка колокольчика на месте, "Записать сделку" справа */}
            {selectedItem && (
              <div className="selected-rows-popup-toolbar selected-rows-popup-toolbar--plan">
                <div
                  className="selected-rows-popup"
                  onClick={handleAlertButtonClick}
                  title="Создать уведомление"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </div>
                <div
                  className="selected-rows-popup"
                  onClick={handleWriteOrderClick}
                  title="Записать сделку"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                  </svg>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {!isPromo && isPlanMobile && isMobileOrderPanelOpen && (() => {
        const selectedItem = selectedRowId
          ? sortedDividends.find(item => selectedRowId === item.id)
          : null;
        if (!selectedItem) return null;

        return (
          <>
            <button
              type="button"
              className="margin-tooltip-backdrop plan-mobile-order-backdrop"
              aria-label="Закрыть"
              onClick={closeMobileOrderPanel}
              onSelectStart={preventModalOverlaySelection}
            />
            <div
              className="plan-mobile-order-overlay"
              role="dialog"
              aria-modal="true"
              onSelectStart={preventModalOverlaySelection}
            >
              <CreateOrderPanel
                stock={selectedItem.stock || '-'}
                futures={selectedItem.futures?.ticker || '-'}
                stockPrice={selectedItem[broker]?.stock?.ask?.price || null}
                futuresPrice={selectedItem[broker]?.futures?.ask?.price || null}
                stockIsTrading={selectedItem[broker]?.stock?.isTrading}
                futuresIsTrading={selectedItem[broker]?.futures?.isTrading}
                lotSize={selectedItem.futures?.lotSize || null}
                broker={selectedBroker}
                onSubmit={handleSubmitOrder}
                percentDo={selectedItem[broker]?.calculatedFields?.maxProfit || null}
                percentExp={selectedItem[broker]?.calculatedFields?.minProfit || null}
                spread={selectedItem[broker]?.calculatedFields?.inputSpread || null}
                onAutofillClick={handleAutofillButtonClick}
                isRowSelected
              />
              <div className="selected-rows-popup-toolbar selected-rows-popup-toolbar--plan plan-mobile-order-overlay__toolbar">
                <div
                  className="selected-rows-popup"
                  onClick={handleAlertButtonClick}
                  title="Создать уведомление"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </div>
                <div
                  className="selected-rows-popup"
                  onClick={handleWriteOrderClick}
                  title="Записать сделку"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                  </svg>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {!isPromo && isAlertModalOpen && (
        <AlertModal
          isOpen={isAlertModalOpen}
          onClose={() => setIsAlertModalOpen(false)}
          onCreateAlert={handleCreateAlert}
          stock={alertInitialValues.stock}
          futures={alertInitialValues.futures}
          defaultStockPrice={alertInitialValues.stockPrice}
          defaultFuturesPrice={alertInitialValues.futuresPrice}
          defaultDivPercent={alertInitialValues.maxProfit}
          defaultExpPercent={alertInitialValues.minProfit}
          defaultSpread={alertInitialValues.inputSpread}
          selectedBroker={selectedBroker}
        />
      )}
      {!isPromo && isAutofillModalOpen && (
        <AutofillModal
          isOpen={isAutofillModalOpen}
          onClose={() => setIsAutofillModalOpen(false)}
          onSubmit={handleCreateAutofillCommand}
          broker={selectedBroker}
          defaultSum={autofillInitialValues.sum}
          defaultPercentDo={autofillInitialValues.percentDo}
          defaultPercentExp={autofillInitialValues.percentExp}
          defaultSpread={autofillInitialValues.spread}
        />
      )}
      {!isPromo && isWriteOrderModalOpen && selectedRowId && (() => {
        const selectedItem = sortedDividends.find(item => selectedRowId === item.id);
        if (!selectedItem) return null;
        const stockPrice = selectedItem[broker]?.stock?.ask?.price;
        const futuresPrice = selectedItem[broker]?.futures?.ask?.price;
        return (
          <WriteOrderModal
            isOpen={isWriteOrderModalOpen}
            onClose={() => setIsWriteOrderModalOpen(false)}
            onSubmit={handleSubmitWriteOrder}
            stock={selectedItem.stock}
            futures={selectedItem.futures.ticker}
            broker={broker}
            stockPrice={stockPrice}
            futuresPrice={futuresPrice}
          />
        );
      })()}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </div>
  );
}

// Провайдер для данных SSE потока
function PlanStreamProvider({ children, userId, userGroup }) {
  const isPromo = userGroup === 'promo';
  const cacheStorageKey = isPromo ? 'planDataCachePromo' : 'planDataCache';

  // Загружаем кэшированные данные при инициализации
  const loadCachedData = () => {
    try {
      const cached = localStorage.getItem(cacheStorageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Проверяем, что кэш не старше 5 минут
        const cacheAge = Date.now() - (parsed.timestamp || 0);
        if (cacheAge < 5 * 60 * 1000) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Error loading cached data:', e);
    }
    return null;
  };

  const cachedData = loadCachedData();
  const [dividends, setDividends] = useState(cachedData?.vectorPlan || []);
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(isPromo ? { keyrate: 0, fairSpreads: [] } : (cachedData?.metadata || { keyrate: 0, fairSpreads: [] }));
  const [triggeredAlerts, setTriggeredAlerts] = useState([]);
  const [marginIndicators, setMarginIndicators] = useState(null);
  const { toasts, pushToast, dismissToast } = useToastStack(5);
  const metadataRef = useRef(isPromo ? { keyrate: 0, fairSpreads: [] } : (cachedData?.metadata || { keyrate: 0, fairSpreads: [] }));
  const pendingCacheRef = useRef(null);
  const lastCacheWriteAtRef = useRef(0);

  useEffect(() => {
    // Подписка на SSE поток
    let reader = null;
    let abortController = null;
    let isMounted = true;
    let reconnectTimeoutId = null;
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 10;
    const BASE_RECONNECT_DELAY = 1000; // 1 секунда
    const CACHE_WRITE_INTERVAL_MS = 10_000;

    const disconnectStream = () => {
      if (reader) {
        reader.cancel().catch(() => {
          // Игнорируем ошибки при отмене
        });
        reader = null;
      }

      if (abortController) {
        abortController.abort();
        abortController = null;
      }
    };

    const flushPlanCache = (force = false) => {
      const pending = pendingCacheRef.current;
      if (!pending) return;
      if (!force && !isMounted) return;
      const now = Date.now();
      if (!force && now - lastCacheWriteAtRef.current < CACHE_WRITE_INTERVAL_MS) return;
      lastCacheWriteAtRef.current = now;
      pendingCacheRef.current = null;
      try {
        localStorage.setItem(cacheStorageKey, JSON.stringify(pending));
      } catch (e) {
        console.error('Error caching data:', e);
      }
    };

    const applySseMessage = (message) => {
      if (isRobotCommandsUpdateMessage(message)) {
        const { commands } = message.data;
        window.dispatchEvent(new CustomEvent('robot.commands.update', {
          detail: Array.isArray(commands) ? commands : [],
        }));
        return;
      }

      if (isNotificationMessage(message)) {
        const { variant, payload } = message.data;
        pushToast(
          formatNotificationMessage(payload),
          variant === 'positive' ? 'success' : 'error',
        );
        return;
      }

      if (isMarginIndicatorsUpdateMessage(message)) {
        setMarginIndicators(message.data);
        window.dispatchEvent(new CustomEvent('margin.indicators.update', {
          detail: message.data,
        }));
        return;
      }

      if (isAlertTriggeredMessage(message)) {
        const { triggeredAlerts: alerts } = message.data;
        setTriggeredAlerts(Array.isArray(alerts) ? alerts : []);
        return;
      }

      if (isPlanUpdateMessage(message)) {
        const { vectorPlan, metadata } = message.data;
        setDividends(vectorPlan);
        setLoading(false);
        setError(null);

        const newMetadata = {
          keyrate: metadata.keyrate || 0,
          fairSpreads: metadata.fairSpreads || [],
        };
        setMetadata(newMetadata);
        metadataRef.current = newMetadata;
        pendingCacheRef.current = {
          vectorPlan,
          metadata: newMetadata,
          timestamp: Date.now(),
        };
        queueMicrotask(() => flushPlanCache(false));
        return;
      }

      if (isPlanUpdatePromoMessage(message)) {
        const { vectorPlan } = message.data;
        setDividends(vectorPlan);
        setLoading(false);
        setError(null);
        pendingCacheRef.current = {
          vectorPlan,
          metadata: { keyrate: 0, fairSpreads: [] },
          timestamp: Date.now(),
        };
        queueMicrotask(() => flushPlanCache(false));
      }
    };

    const isHeavyPlanMessage = (message) => (
      isPlanUpdateMessage(message) || isPlanUpdatePromoMessage(message)
    );
    
    const connectToStream = () => {
      const token = authService.getAccessToken();
      
      if (!token) {
        return;
      }

      disconnectStream();
      abortController = new AbortController();
      
      const url = SERVER_URL + (isPromo ? '/v1/plan/stream-promo' : '/v1/plan/stream');
      const headers = authService.getAuthHeaders();
      
      // Используем fetch для SSE с поддержкой заголовков авторизации
      fetch(url, {
        signal: abortController.signal,
        headers: {
          ...headers,
          'Accept': 'text/event-stream',
        },
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`SSE connection failed: ${response.status}`);
        }
        
        // Успешное подключение - сбрасываем счетчик попыток
        reconnectAttempts = 0;
        reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        
        const readStream = () => {
          if (!isMounted) {
            return;
          }
          
          reader.read().then(({ done, value }) => {
            if (done) {
              console.log('SSE stream closed, attempting to reconnect...');
              // Поток закрыт - пытаемся переподключиться
              if (isMounted) {
                scheduleReconnect();
              }
              return;
            }
            
            if (!isMounted) {
              return;
            }
            
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Оставляем незавершенную строку в буфере

            // Сначала лёгкие сообщения (robot/margin/alerts), потом тяжёлый plan —
            // иначе localStorage/parse плана блокирует обновление робокоманд.
            const parsed = [];
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              const message = parseSseMessage(line.slice(6));
              if (!message) {
                console.error('Unknown or invalid SSE message');
                continue;
              }
              parsed.push(message);
            }
            for (const message of parsed) {
              if (!isHeavyPlanMessage(message)) applySseMessage(message);
            }
            for (const message of parsed) {
              if (isHeavyPlanMessage(message)) applySseMessage(message);
            }
            
            readStream();
          }).catch((err) => {
            if (!isMounted || err.name === 'AbortError') {
              return;
            }
            console.error('SSE stream error:', err);
            setError(err.message);
            // Пытаемся переподключиться при ошибке
            scheduleReconnect();
          });
        };
        
        readStream();
      }).catch((err) => {
        if (!isMounted || err.name === 'AbortError') {
          return;
        }
        console.error('Failed to connect to SSE stream:', err);
        setError(err.message);
        setLoading(false);
        // Пытаемся переподключиться при ошибке подключения
        scheduleReconnect();
      });
    };
    
    // Функция для планирования переподключения с экспоненциальной задержкой
    const scheduleReconnect = () => {
      if (!isMounted) {
        return;
      }
      
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.error('Max reconnection attempts reached');
        setError('Не удалось подключиться к потоку данных после нескольких попыток');
        return;
      }
      
      // Экспоненциальная задержка: 1s, 2s, 4s, 8s, ... до максимума 30s
      const delay = Math.min(BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts), 30000);
      reconnectAttempts++;
      
      console.log(`Attempting to reconnect in ${delay}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`);
      
      reconnectTimeoutId = setTimeout(() => {
        if (isMounted) {
          connectToStream();
        }
      }, delay);
    };
    
    // Обработчик события видимости вкладки
    const handleVisibilityChange = () => {
      if (!isMounted) {
        return;
      }
      
      // Если вкладка стала видимой и соединение разорвано, переподключаемся
      if (document.visibilityState === 'visible') {
        // Проверяем, есть ли активное соединение
        // Если reader закрыт или нет активного соединения, переподключаемся
        if (!reader || reconnectTimeoutId) {
          // Отменяем запланированное переподключение и подключаемся сразу
          if (reconnectTimeoutId) {
            clearTimeout(reconnectTimeoutId);
            reconnectTimeoutId = null;
          }
          reconnectAttempts = 0; // Сбрасываем счетчик при ручном переподключении
          console.log('Tab became visible, reconnecting to SSE stream...');
          connectToStream();
        }
      }
    };
    
    // Подключаемся при монтировании
    connectToStream();
    
    // Добавляем обработчик события видимости
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId);
      }

      flushPlanCache(true);
      isMounted = false;
      disconnectStream();
    };
  }, [isPromo, cacheStorageKey, pushToast]); // Переподключаемся при смене типа потока

  return (
    <PlanStreamContext.Provider value={{ dividends, metadata, loading, error, triggeredAlerts, marginIndicators, userId, isPromo }}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </PlanStreamContext.Provider>
  );
}

function AdministrationPage() {
  const ADMIN_ROW_LONG_PRESS_MS = 450;

  const [observables, setObservables] = useState([]);
  const [dividends, setDividends] = useState([]);
  const [selectedObservableIds, setSelectedObservableIds] = useState(new Set());
  const [selectedDividendIds, setSelectedDividendIds] = useState(new Set());
  const [observableForm, setObservableForm] = useState({ stock: '', futures: '', isHidden: false, forPromo: false });
  const [dividendForm, setDividendForm] = useState({
    stock: '',
    size: '',
    date: '',
    approved: false,
    dontTake: false,
    url: ''
  });
  const [dividendFormErrors, setDividendFormErrors] = useState({});
  const [isObservableAddModalOpen, setIsObservableAddModalOpen] = useState(false);
  const [isDividendAddModalOpen, setIsDividendAddModalOpen] = useState(false);
  const [editingObservableId, setEditingObservableId] = useState(null);
  const [editingDividendId, setEditingDividendId] = useState(null);
  const [isDividendDatePickerOpen, setIsDividendDatePickerOpen] = useState(false);
  const [dividendDateDefault, setDividendDateDefault] = useState('');
  const dividendDatePickerRef = useRef(null);
  const [collapsedDividendGroups, setCollapsedDividendGroups] = useState(new Set());
  const hasInitializedDividendGroups = useRef(false);
  const [loading, setLoading] = useState(true);
  const [observableSortConfig, setObservableSortConfig] = usePersistentState('administration.sortConfig', { key: null, direction: 'asc' });
  const [dividendSortConfig, setDividendSortConfig] = usePersistentState('administration.dividendsSortConfig', { key: null, direction: 'asc' });
  const [groupDividendsByStock, setGroupDividendsByStock] = usePersistentState('administration.groupDividendsByStock', false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' });
  const [isAdminMobile, setIsAdminMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY).matches
  );
  const observableLongPressRef = useRef({ timer: null, triggered: false, rowId: null });
  const dividendLongPressRef = useRef({ timer: null, triggered: false, rowId: null });

  useModalSelectionGuard(isObservableAddModalOpen);
  useModalSelectionGuard(isDividendAddModalOpen);

  const observablesUrl = SERVER_URL + '/v1/administration/observables';
  const dividendsUrl = SERVER_URL + '/v1/administration/dividends';

  const emptyDividendForm = {
    stock: '',
    size: '',
    date: '',
    approved: false,
    dontTake: false,
    url: ''
  };

  const showToast = (message, type = 'success') => {
    setToast({ isVisible: true, message, type });
  };

  const hideToast = () => {
    setToast((previous) => ({ ...previous, isVisible: false }));
  };

  const toastErrorText = (error) => (error?.message != null ? String(error.message) : String(error));

  const toDateInputValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value.slice(0, 10);
    return new Date(value).toISOString().slice(0, 10);
  };

  const toDividendPayload = (data) => ({
    stock: data.stock,
    size: Number(data.size),
    date: data.date,
    approved: Boolean(data.approved),
    dontTake: Boolean(data.dontTake),
    url: data.url?.trim() || null
  });

  const validateDividendForm = (data) => {
    const errors = {};

    if (!data.stock.trim()) {
      errors.stock = 'Заполните это поле';
    }
    if (String(data.size).trim() === '') {
      errors.size = 'Заполните это поле';
    }
    if (!data.date) {
      errors.date = 'Заполните это поле';
    } else if (!DATE_VALUE_PATTERN.test(data.date)) {
      errors.date = 'Введите корректную дату';
    }
    if (!data.url.trim()) {
      errors.url = 'Заполните это поле';
    } else {
      try {
        new URL(data.url);
      } catch {
        errors.url = 'Введите ссылку в формате https://...';
      }
    }

    return errors;
  };

  const handleDividendFormChange = (field, value) => {
    setDividendForm((previous) => ({ ...previous, [field]: value }));
    setDividendFormErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }
      const next = { ...previous };
      delete next[field];
      return next;
    });
  };

  const loadAdministrationData = useCallback(async () => {
    try {
      const [observablesResponse, dividendsResponse] = await Promise.all([
        authService.authenticatedRequest(observablesUrl),
        authService.authenticatedRequest(dividendsUrl)
      ]);
      setObservables(await observablesResponse.json());
      setDividends(await dividendsResponse.json());
    } catch (error) {
      showToast(toastErrorText(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [dividendsUrl, observablesUrl]);

  useEffect(() => {
    loadAdministrationData();
  }, [loadAdministrationData]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY);
    const handleChange = (event) => setIsAdminMobile(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const clearObservableLongPress = useCallback(() => {
    const pressState = observableLongPressRef.current;
    if (pressState.timer) {
      clearTimeout(pressState.timer);
      pressState.timer = null;
    }
  }, []);

  const clearDividendLongPress = useCallback(() => {
    const pressState = dividendLongPressRef.current;
    if (pressState.timer) {
      clearTimeout(pressState.timer);
      pressState.timer = null;
    }
  }, []);

  useEffect(() => () => {
    clearObservableLongPress();
    clearDividendLongPress();
  }, [clearDividendLongPress, clearObservableLongPress]);

  const selectedObservable = useMemo(() => {
    if (selectedObservableIds.size !== 1) {
      return null;
    }
    const id = [...selectedObservableIds][0];
    return observables.find((observable) => observable.id === id) ?? null;
  }, [observables, selectedObservableIds]);

  const selectedDividend = useMemo(() => {
    if (selectedDividendIds.size !== 1) {
      return null;
    }
    const id = [...selectedDividendIds][0];
    return dividends.find((dividend) => dividend.id === id) ?? null;
  }, [dividends, selectedDividendIds]);

  const handleObservableSort = (key) => {
    setObservableSortConfig((previous) => {
      if (previous.key === key) {
        return { key, direction: previous.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const handleDividendSort = (key) => {
    setDividendSortConfig((previous) => {
      if (previous.key === key) {
        return { key, direction: previous.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortedObservables = useMemo(() => {
    if (!observableSortConfig.key) {
      return observables;
    }
    const directionMultiplier = observableSortConfig.direction === 'asc' ? 1 : -1;
    return [...observables].sort((a, b) => {
      let comparison = 0;
      switch (observableSortConfig.key) {
        case 'stock':
          comparison = String(a.stock).localeCompare(String(b.stock), 'ru', { sensitivity: 'base' });
          break;
        case 'futures':
          comparison = String(a.futures).localeCompare(String(b.futures), 'ru', { sensitivity: 'base' });
          break;
        case 'available': {
          const hiddenRank = (row) => (row.isHidden ? 1 : 0);
          comparison = hiddenRank(a) - hiddenRank(b);
          break;
        }
        case 'forPromo': {
          const promoRank = (row) => (row.forPromo ? 1 : 0);
          comparison = promoRank(a) - promoRank(b);
          break;
        }
        default:
          return 0;
      }
      return comparison * directionMultiplier;
    });
  }, [observables, observableSortConfig]);

  const sortedDividends = useMemo(() => {
    if (!dividendSortConfig.key) {
      return dividends;
    }
    const directionMultiplier = dividendSortConfig.direction === 'asc' ? 1 : -1;
    return [...dividends].sort((a, b) => {
      let comparison = 0;
      switch (dividendSortConfig.key) {
        case 'stock':
          comparison = String(a.stock).localeCompare(String(b.stock), 'ru', { sensitivity: 'base' });
          break;
        case 'size':
          comparison = Number(a.size) - Number(b.size);
          break;
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'approved':
          comparison = Number(Boolean(a.approved)) - Number(Boolean(b.approved));
          break;
        case 'dontTake':
          comparison = Number(Boolean(a.dontTake)) - Number(Boolean(b.dontTake));
          break;
        default:
          return 0;
      }
      return comparison * directionMultiplier;
    });
  }, [dividends, dividendSortConfig]);

  const groupedDividendStocks = useMemo(() => {
    if (!groupDividendsByStock) {
      return [];
    }

    return Array.from(new Set(sortedDividends.map((dividend) => dividend.stock || '-')));
  }, [groupDividendsByStock, sortedDividends]);

  const dividendTableRows = useMemo(() => {
    if (!groupDividendsByStock) {
      return sortedDividends.map((dividend) => ({ type: 'dividend', dividend }));
    }

    const grouped = sortedDividends.reduce((accumulator, dividend) => {
      const stock = dividend.stock || '-';
      if (!accumulator.has(stock)) {
        accumulator.set(stock, []);
      }
      accumulator.get(stock).push(dividend);
      return accumulator;
    }, new Map());

    return Array.from(grouped.entries())
      .sort(([stockA], [stockB]) => String(stockA).localeCompare(String(stockB), 'ru', { sensitivity: 'base' }))
      .flatMap(([stock, stockDividends]) => {
        const groupRow = { type: 'group', stock, count: stockDividends.length };
        if (collapsedDividendGroups.has(stock)) {
          return [groupRow];
        }
        return [
          groupRow,
          ...stockDividends.map((dividend) => ({ type: 'dividend', dividend }))
        ];
      });
  }, [collapsedDividendGroups, groupDividendsByStock, sortedDividends]);

  const toggleDividendGrouping = (isEnabled) => {
    setGroupDividendsByStock(isEnabled);
    setCollapsedDividendGroups(isEnabled ? new Set(groupedDividendStocks) : new Set());
  };

  const toggleDividendGroup = (stock) => {
    setCollapsedDividendGroups((previous) => {
      const next = new Set(previous);
      if (next.has(stock)) {
        next.delete(stock);
      } else {
        next.add(stock);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!groupDividendsByStock) {
      hasInitializedDividendGroups.current = false;
      return;
    }
    if (hasInitializedDividendGroups.current || groupedDividendStocks.length === 0) {
      return;
    }

    hasInitializedDividendGroups.current = true;
    setCollapsedDividendGroups(new Set(groupedDividendStocks));
  }, [groupDividendsByStock, groupedDividendStocks]);

  const SortableHeader = ({ children, sortKey, sortConfig, onSort }) => (
    <th
      className={`sortable ${sortConfig.key === sortKey ? 'active' : ''}`}
      onClick={() => onSort(sortKey)}
      title="Кликните для сортировки"
    >
      {children}
    </th>
  );

  const handleObservableSelect = (id) => {
    setSelectedObservableIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDividendSelect = (id) => {
    setSelectedDividendIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleObservableRowSelect = (id) => {
    if (isAdminMobile) {
      setSelectedObservableIds((previous) => {
        if (previous.has(id) && previous.size === 1) {
          return new Set();
        }
        return new Set([id]);
      });
      return;
    }
    handleObservableSelect(id);
  };

  const handleDividendRowSelect = (id) => {
    if (isAdminMobile) {
      setSelectedDividendIds((previous) => {
        if (previous.has(id) && previous.size === 1) {
          return new Set();
        }
        return new Set([id]);
      });
      return;
    }
    handleDividendSelect(id);
  };

  const handleObservableRowClick = (observable, event) => {
    if (!isAdminMobile) {
      return;
    }
    if (event.target.closest('input, button, textarea, select, a')) {
      return;
    }

    const pressState = observableLongPressRef.current;
    if (pressState.triggered && pressState.rowId === observable.id) {
      pressState.triggered = false;
      return;
    }

    handleObservableRowSelect(observable.id);
  };

  const handleDividendRowClick = (dividend, event) => {
    if (!isAdminMobile) {
      return;
    }
    if (event.target.closest('input, button, textarea, select, a')) {
      return;
    }

    const pressState = dividendLongPressRef.current;
    if (pressState.triggered && pressState.rowId === dividend.id) {
      pressState.triggered = false;
      return;
    }

    handleDividendRowSelect(dividend.id);
  };

  const handleObservableSelectAll = () => {
    setSelectedObservableIds((previous) => {
      if (previous.size === observables.length) {
        return new Set();
      }
      return new Set(observables.map((observable) => observable.id));
    });
  };

  const handleDividendSelectAll = () => {
    setSelectedDividendIds((previous) => {
      if (previous.size === dividends.length) {
        return new Set();
      }
      return new Set(dividends.map((dividend) => dividend.id));
    });
  };

  const openObservableAddModal = () => {
    setEditingObservableId(null);
    setObservableForm({ stock: '', futures: '', isHidden: false, forPromo: false });
    setIsObservableAddModalOpen(true);
  };

  const openObservableEditModal = (observable) => {
    clearTextSelection();
    setEditingObservableId(observable.id);
    setObservableForm({
      stock: observable.stock,
      futures: observable.futures,
      isHidden: Boolean(observable.isHidden),
      forPromo: Boolean(observable.forPromo)
    });
    setIsObservableAddModalOpen(true);
  };

  const closeObservableAddModal = () => {
    setEditingObservableId(null);
    setIsObservableAddModalOpen(false);
  };

  const openDividendAddModal = () => {
    setEditingDividendId(null);
    setDividendForm(emptyDividendForm);
    setDividendFormErrors({});
    setDividendDateDefault('');
    setIsDividendDatePickerOpen(false);
    setIsDividendAddModalOpen(true);
  };

  const openDividendEditModal = (dividend) => {
    clearTextSelection();
    const dateValue = toDateInputValue(dividend.date);
    setEditingDividendId(dividend.id);
    setDividendForm({
      stock: dividend.stock,
      size: dividend.size,
      date: dateValue,
      approved: Boolean(dividend.approved),
      dontTake: Boolean(dividend.dontTake),
      url: dividend.url ?? ''
    });
    setDividendFormErrors({});
    setDividendDateDefault(dateValue);
    setIsDividendDatePickerOpen(false);
    setIsDividendAddModalOpen(true);
  };

  const closeDividendAddModal = () => {
    setEditingDividendId(null);
    setDividendFormErrors({});
    setIsDividendDatePickerOpen(false);
    setIsDividendAddModalOpen(false);
  };

  useEffect(() => {
    if (!isObservableAddModalOpen && !isDividendAddModalOpen) {
      return undefined;
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isDividendAddModalOpen && isDividendDatePickerOpen) {
          setIsDividendDatePickerOpen(false);
          return;
        }
        setIsObservableAddModalOpen(false);
        closeDividendAddModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDividendAddModalOpen, isDividendDatePickerOpen, isObservableAddModalOpen]);

  useEffect(() => {
    if (!isDividendDatePickerOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!dividendDatePickerRef.current?.contains(event.target)) {
        setIsDividendDatePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isDividendDatePickerOpen]);

  const handleObservableSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingObservableId) {
        await authService.authenticatedRequest(`${observablesUrl}/${editingObservableId}`, {
          method: 'PATCH',
          body: JSON.stringify(observableForm)
        });
        showToast('Запись обновлена', 'success');
      } else {
        await authService.authenticatedRequest(observablesUrl, {
          method: 'POST',
          body: JSON.stringify(observableForm)
        });
        showToast('Запись добавлена', 'success');
      }
      await loadAdministrationData();
      closeObservableAddModal();
    } catch (error) {
      showToast(toastErrorText(error), 'error');
    }
  };

  const handleDividendSubmit = async (event) => {
    event.preventDefault();

    const errors = validateDividendForm(dividendForm);
    setDividendFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      if (editingDividendId) {
        await authService.authenticatedRequest(`${dividendsUrl}/${editingDividendId}`, {
          method: 'PATCH',
          body: JSON.stringify(toDividendPayload(dividendForm))
        });
        showToast('Дивиденд обновлен', 'success');
      } else {
        await authService.authenticatedRequest(dividendsUrl, {
          method: 'POST',
          body: JSON.stringify(toDividendPayload(dividendForm))
        });
        showToast('Дивиденд добавлен', 'success');
      }
      await loadAdministrationData();
      closeDividendAddModal();
    } catch (error) {
      showToast(toastErrorText(error), 'error');
    }
  };

  const handleObservableRowTouchStart = (observable) => {
    if (!isAdminMobile) {
      return;
    }

    const pressState = observableLongPressRef.current;
    pressState.triggered = false;
    pressState.rowId = observable.id;
    clearObservableLongPress();
    pressState.timer = window.setTimeout(() => {
      pressState.timer = null;
      pressState.triggered = true;
      clearTextSelection();
      setSelectedObservableIds(new Set());
      openObservableEditModal(observable);
    }, ADMIN_ROW_LONG_PRESS_MS);
  };

  const handleDividendRowTouchStart = (dividend) => {
    if (!isAdminMobile) {
      return;
    }

    const pressState = dividendLongPressRef.current;
    pressState.triggered = false;
    pressState.rowId = dividend.id;
    clearDividendLongPress();
    pressState.timer = window.setTimeout(() => {
      pressState.timer = null;
      pressState.triggered = true;
      clearTextSelection();
      setSelectedDividendIds(new Set());
      openDividendEditModal(dividend);
    }, ADMIN_ROW_LONG_PRESS_MS);
  };

  const handleObservableRowTouchEnd = (event) => {
    const pressState = observableLongPressRef.current;
    if (pressState.triggered) {
      event?.preventDefault?.();
      clearTextSelection();
    }
    clearObservableLongPress();
  };

  const handleDividendRowTouchEnd = (event) => {
    const pressState = dividendLongPressRef.current;
    if (pressState.triggered) {
      event?.preventDefault?.();
      clearTextSelection();
    }
    clearDividendLongPress();
  };

  const deleteSelectedObservables = () => {
    const ids = Array.from(selectedObservableIds);
    if (ids.length === 0) return;

    setConfirmDialog({
      message: `Удалить выбранные записи: ${ids.length}?`,
      confirmLabel: 'Удалить',
      confirmVariant: 'danger',
      onConfirm: async () => {
        try {
          await authService.authenticatedRequest(observablesUrl, {
            method: 'DELETE',
            body: JSON.stringify({ ids })
          });
          setSelectedObservableIds(new Set());
          await loadAdministrationData();
          showToast('Выбранные записи удалены', 'success');
        } catch (error) {
          showToast(toastErrorText(error), 'error');
        }
      }
    });
  };

  const deleteSelectedDividends = () => {
    const ids = Array.from(selectedDividendIds);
    if (ids.length === 0) return;

    setConfirmDialog({
      message: `Удалить выбранные дивиденды: ${ids.length}?`,
      confirmLabel: 'Удалить',
      confirmVariant: 'danger',
      onConfirm: async () => {
        try {
          await authService.authenticatedRequest(dividendsUrl, {
            method: 'DELETE',
            body: JSON.stringify({ ids })
          });
          setSelectedDividendIds(new Set());
          await loadAdministrationData();
          showToast('Выбранные дивиденды удалены', 'success');
        } catch (error) {
          showToast(toastErrorText(error), 'error');
        }
      }
    });
  };

  const updateSelectedDontTake = async (dontTake) => {
    const ids = Array.from(selectedDividendIds);
    if (ids.length === 0) return;

    try {
      await authService.authenticatedRequest(`${dividendsUrl}/dont-take`, {
        method: 'PATCH',
        body: JSON.stringify({ ids, dontTake })
      });
      await loadAdministrationData();
      showToast(dontTake ? 'Флаг актуальности сброшен' : 'Флаг актуальности установлен', 'success');
    } catch (error) {
      showToast(toastErrorText(error), 'error');
    }
  };

  const updateSelectedApproved = async (approved) => {
    const ids = Array.from(selectedDividendIds);
    if (ids.length === 0) return;

    try {
      await authService.authenticatedRequest(`${dividendsUrl}/approved`, {
        method: 'PATCH',
        body: JSON.stringify({ ids, approved })
      });
      await loadAdministrationData();
      showToast(approved ? 'Дивиденды отмечены как утвержденные/рекомендованные' : 'Дивиденды отмечены как прогноз', 'success');
    } catch (error) {
      showToast(toastErrorText(error), 'error');
    }
  };

  const updateSelectedAvailability = async (isAvailable) => {
    const ids = Array.from(selectedObservableIds);
    if (ids.length === 0) return;

    try {
      await authService.authenticatedRequest(`${observablesUrl}/availability`, {
        method: 'PATCH',
        body: JSON.stringify({ ids, isAvailable })
      });
      await loadAdministrationData();
      showToast(isAvailable ? 'Доступность установлена' : 'Доступность сброшена', 'success');
    } catch (error) {
      showToast(toastErrorText(error), 'error');
    }
  };

  const updateSelectedForPromo = async (forPromo) => {
    const ids = Array.from(selectedObservableIds);
    if (ids.length === 0) return;

    try {
      await authService.authenticatedRequest(`${observablesUrl}/for-promo`, {
        method: 'PATCH',
        body: JSON.stringify({ ids, forPromo })
      });
      await loadAdministrationData();
      showToast(forPromo ? 'Promo включён для выбранных активов' : 'Promo выключен для выбранных активов', 'success');
    } catch (error) {
      showToast(toastErrorText(error), 'error');
    }
  };

  if (loading) {
    return (
      <div className="App">
        <div className="container administration-container"><div className="loading">Загрузка...</div></div>
        <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
      </div>
    );
  }

  return (
    <div className="App">
      <div className={`administration-page${isAdminMobile ? ' administration-page--mobile-selection' : ''}`}>
        <div className="administration-split">
          <section className="container administration-container administration-panel administration-assets-panel">
            <h2 className="administration-panel-title">Активы</h2>
            <div className="administration-toolbar">
              {isAdminMobile ? (
                <>
                  <button
                    type="button"
                    onClick={() => updateSelectedAvailability(Boolean(selectedObservable?.isHidden))}
                    disabled={!selectedObservable}
                  >
                    {selectedObservable?.isHidden ? 'Доступен' : 'Скрыть'}
                  </button>
                  <button
                    type="button"
                    onClick={() => updateSelectedForPromo(!selectedObservable?.forPromo)}
                    disabled={!selectedObservable}
                  >
                    {selectedObservable?.forPromo ? 'Promo-' : 'Promo+'}
                  </button>
                  <div className="administration-toolbar-actions">
                    <button
                      type="button"
                      className="administration-toolbar-add administration-button--success"
                      onClick={openObservableAddModal}
                      title="Добавить актив"
                      aria-label="Добавить актив"
                    >
                      Добавить
                    </button>
                    <button
                      type="button"
                      className="administration-button--danger"
                      onClick={deleteSelectedObservables}
                      disabled={!selectedObservable}
                    >
                      Удалить
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="administration-toolbar-group">
                    <button type="button" onClick={() => updateSelectedAvailability(true)} disabled={selectedObservableIds.size === 0}>
                      Доступен
                    </button>
                    <button type="button" onClick={() => updateSelectedAvailability(false)} disabled={selectedObservableIds.size === 0}>
                      Скрыть
                    </button>
                  </div>
                  <div className="administration-toolbar-group">
                    <button type="button" onClick={() => updateSelectedForPromo(true)} disabled={selectedObservableIds.size === 0}>
                      Promo+
                    </button>
                    <button type="button" onClick={() => updateSelectedForPromo(false)} disabled={selectedObservableIds.size === 0}>
                      Promo-
                    </button>
                  </div>
                  <button
                    type="button"
                    className="administration-button--danger"
                    onClick={deleteSelectedObservables}
                    disabled={selectedObservableIds.size === 0}
                  >
                    Удалить
                  </button>
                  <button
                    type="button"
                    className="administration-toolbar-add"
                    onClick={openObservableAddModal}
                    title="Добавить актив"
                    aria-label="Добавить актив"
                  >
                    Добавить
                  </button>
                </>
              )}
            </div>

            <div className="table-container administration-table-container">
              <table className="dividend-table administration-table administration-assets-table">
                <thead>
                  <tr>
                    {!isAdminMobile && (
                      <th>
                        <input
                          type="checkbox"
                          checked={observables.length > 0 && selectedObservableIds.size === observables.length}
                          onChange={handleObservableSelectAll}
                        />
                      </th>
                    )}
                    <SortableHeader sortKey="stock" sortConfig={observableSortConfig} onSort={handleObservableSort}>Акция</SortableHeader>
                    <SortableHeader sortKey="futures" sortConfig={observableSortConfig} onSort={handleObservableSort}>Фьючерс</SortableHeader>
                    <SortableHeader sortKey="available" sortConfig={observableSortConfig} onSort={handleObservableSort}>Доступен</SortableHeader>
                    <SortableHeader sortKey="forPromo" sortConfig={observableSortConfig} onSort={handleObservableSort}>Promo</SortableHeader>
                    {!isAdminMobile && <th>Действия</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedObservables.map((observable) => (
                      <tr
                        key={observable.id}
                        className={[
                          selectedObservableIds.has(observable.id) ? 'selected-row' : '',
                          isAdminMobile ? 'administration-row--selectable' : ''
                        ].filter(Boolean).join(' ') || undefined}
                        style={isAdminMobile ? { cursor: 'pointer' } : undefined}
                        onClick={isAdminMobile ? (event) => handleObservableRowClick(observable, event) : undefined}
                        onDoubleClick={!isAdminMobile ? (e) => {
                          if (e.target.closest('input, button, textarea, select')) {
                            return;
                          }
                          handleObservableSelect(observable.id);
                        } : undefined}
                        onTouchStart={isAdminMobile ? () => handleObservableRowTouchStart(observable) : undefined}
                        onTouchEnd={isAdminMobile ? (event) => handleObservableRowTouchEnd(event) : undefined}
                        onTouchMove={isAdminMobile ? handleObservableRowTouchEnd : undefined}
                        onTouchCancel={isAdminMobile ? (event) => handleObservableRowTouchEnd(event) : undefined}
                        onContextMenu={isAdminMobile ? (event) => event.preventDefault() : undefined}
                        onSelectStart={preventModalOverlaySelection}
                        onMouseDown={(event) => {
                          if (event.target.closest('input, button, textarea, select')) return;
                          clearTextSelection();
                        }}
                      >
                        {!isAdminMobile && (
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedObservableIds.has(observable.id)}
                              onChange={() => handleObservableSelect(observable.id)}
                            />
                          </td>
                        )}
                        <td>{observable.stock}</td>
                        <td>{observable.futures}</td>
                        <td>{observable.isHidden ? 'Нет' : 'Да'}</td>
                        <td>{observable.forPromo ? 'Да' : 'Нет'}</td>
                        {!isAdminMobile && (
                          <td>
                            <button type="button" onClick={() => openObservableEditModal(observable)}>Редактировать</button>
                          </td>
                        )}
                      </tr>
                  ))}
                  {observables.length === 0 && (
                    <tr>
                      <td colSpan={isAdminMobile ? 4 : 6} style={{ textAlign: 'center' }}>
                        Нет данных
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="container administration-container administration-panel administration-dividends-panel">
            <h2 className="administration-panel-title">Дивиденды</h2>
            <div className="administration-toolbar">
              {isAdminMobile ? (
                <>
                  <button
                    type="button"
                    onClick={() => updateSelectedApproved(!selectedDividend?.approved)}
                    disabled={!selectedDividend}
                  >
                    {selectedDividend?.approved ? 'Прогноз' : 'Утв./рек.'}
                  </button>
                  <button
                    type="button"
                    onClick={() => updateSelectedDontTake(!selectedDividend?.dontTake)}
                    disabled={!selectedDividend}
                  >
                    {selectedDividend?.dontTake ? 'Правдив' : 'Не актуален'}
                  </button>
                  <label
                    className="approved-dividends-toggle administration-group-toggle"
                    title={groupDividendsByStock ? 'Отключить группировку по акции' : 'Включить группировку по акции'}
                  >
                    <input
                      type="checkbox"
                      checked={groupDividendsByStock}
                      onChange={(event) => toggleDividendGrouping(event.target.checked)}
                      aria-label="Группировать дивиденды по акции"
                    />
                    <span className="approved-dividends-toggle__slider" aria-hidden="true"></span>
                  </label>
                  <div className="administration-toolbar-actions">
                    <button
                      type="button"
                      className="administration-toolbar-add administration-button--success"
                      onClick={openDividendAddModal}
                      title="Добавить дивиденд"
                      aria-label="Добавить дивиденд"
                    >
                      Добавить
                    </button>
                    <button
                      type="button"
                      className="administration-button--danger"
                      onClick={deleteSelectedDividends}
                      disabled={!selectedDividend}
                    >
                      Удалить
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="administration-toolbar-group">
                    <button type="button" onClick={() => updateSelectedApproved(true)} disabled={selectedDividendIds.size === 0}>
                      Утв./рек.
                    </button>
                    <button type="button" onClick={() => updateSelectedApproved(false)} disabled={selectedDividendIds.size === 0}>
                      Прогноз
                    </button>
                  </div>
                  <div className="administration-toolbar-group">
                    <button type="button" onClick={() => updateSelectedDontTake(true)} disabled={selectedDividendIds.size === 0}>
                      Не актуален
                    </button>
                    <button type="button" onClick={() => updateSelectedDontTake(false)} disabled={selectedDividendIds.size === 0}>
                      Правдив
                    </button>
                  </div>
                  <button
                    type="button"
                    className="administration-button--danger"
                    onClick={deleteSelectedDividends}
                    disabled={selectedDividendIds.size === 0}
                  >
                    Удалить
                  </button>
                  <label
                    className="approved-dividends-toggle administration-group-toggle"
                    title={groupDividendsByStock ? 'Отключить группировку по акции' : 'Включить группировку по акции'}
                  >
                    <input
                      type="checkbox"
                      checked={groupDividendsByStock}
                      onChange={(event) => toggleDividendGrouping(event.target.checked)}
                      aria-label="Группировать дивиденды по акции"
                    />
                    <span className="approved-dividends-toggle__slider" aria-hidden="true"></span>
                  </label>
                  <button
                    type="button"
                    className="administration-toolbar-add"
                    onClick={openDividendAddModal}
                    title="Добавить дивиденд"
                    aria-label="Добавить дивиденд"
                  >
                    Добавить
                  </button>
                </>
              )}
            </div>

            <div className="table-container administration-table-container">
              <table className="dividend-table administration-table administration-dividends-table">
                <thead>
                  <tr>
                    {!isAdminMobile && (
                      <th>
                        <input
                          type="checkbox"
                          checked={dividends.length > 0 && selectedDividendIds.size === dividends.length}
                          onChange={handleDividendSelectAll}
                        />
                      </th>
                    )}
                    <SortableHeader sortKey="stock" sortConfig={dividendSortConfig} onSort={handleDividendSort}>Акция</SortableHeader>
                    <SortableHeader sortKey="size" sortConfig={dividendSortConfig} onSort={handleDividendSort}>Размер</SortableHeader>
                    <SortableHeader sortKey="date" sortConfig={dividendSortConfig} onSort={handleDividendSort}>Дата</SortableHeader>
                    <SortableHeader sortKey="approved" sortConfig={dividendSortConfig} onSort={handleDividendSort}>Рек.</SortableHeader>
                    <SortableHeader sortKey="dontTake" sortConfig={dividendSortConfig} onSort={handleDividendSort}>Ложь</SortableHeader>
                    <th>Источник</th>
                    {!isAdminMobile && <th>Действия</th>}
                  </tr>
                </thead>
                <tbody>
                  {dividendTableRows.map((row) => {
                    if (row.type === 'group') {
                      return (
                        <tr
                          key={`group-${row.stock}`}
                          className="group-header"
                          style={{
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                          onSelectStart={preventModalOverlaySelection}
                          onClick={() => {
                            clearTextSelection();
                            toggleDividendGroup(row.stock);
                          }}
                        >
                          {!isAdminMobile && <td />}
                          <td style={{ fontWeight: 'normal', paddingLeft: isAdminMobile ? '12px' : '20px' }}>
                            <span>{row.stock}</span>
                          </td>
                          <td colSpan={isAdminMobile ? 5 : 6} />
                        </tr>
                      );
                    }

                    const { dividend } = row;
                    return (
                      <tr
                        key={dividend.id}
                        className={[
                          selectedDividendIds.has(dividend.id) ? 'selected-row' : '',
                          isAdminMobile ? 'administration-row--selectable' : ''
                        ].filter(Boolean).join(' ') || undefined}
                        style={isAdminMobile ? { cursor: 'pointer' } : undefined}
                        onClick={isAdminMobile ? (event) => handleDividendRowClick(dividend, event) : undefined}
                        onDoubleClick={!isAdminMobile ? (e) => {
                          if (e.target.closest('input, button, textarea, select, a')) {
                            return;
                          }
                          handleDividendSelect(dividend.id);
                        } : undefined}
                        onTouchStart={isAdminMobile ? () => handleDividendRowTouchStart(dividend) : undefined}
                        onTouchEnd={isAdminMobile ? (event) => handleDividendRowTouchEnd(event) : undefined}
                        onTouchMove={isAdminMobile ? handleDividendRowTouchEnd : undefined}
                        onTouchCancel={isAdminMobile ? (event) => handleDividendRowTouchEnd(event) : undefined}
                        onContextMenu={isAdminMobile ? (event) => event.preventDefault() : undefined}
                        onSelectStart={preventModalOverlaySelection}
                        onMouseDown={(event) => {
                          if (event.target.closest('input, button, textarea, select, a')) return;
                          clearTextSelection();
                        }}
                      >
                        {!isAdminMobile && (
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedDividendIds.has(dividend.id)}
                              onChange={() => handleDividendSelect(dividend.id)}
                            />
                          </td>
                        )}
                        <td>{dividend.stock}</td>
                        <td>{formatNumber(dividend.size, 4)}</td>
                        <td>{formatDate(dividend.date)}</td>
                        <td>{dividend.approved ? 'Да' : 'Нет'}</td>
                        <td>{dividend.dontTake ? 'Да' : 'Нет'}</td>
                        <td>
                          {dividend.url ? (
                            <a href={dividend.url} target="_blank" rel="noreferrer" className="company-link administration-source-link">
                              {dividend.url}
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        {!isAdminMobile && (
                          <td>
                            <button type="button" onClick={() => openDividendEditModal(dividend)}>Редактировать</button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {dividends.length === 0 && (
                    <tr>
                      <td colSpan={isAdminMobile ? 6 : 8} style={{ textAlign: 'center' }}>
                        Нет данных
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>

      {isObservableAddModalOpen && (
        <div className="secrets-modal-overlay" onSelectStart={preventModalOverlaySelection}>
          <div className="secrets-modal">
            <div className="secrets-modal-header">
              <h2>{editingObservableId ? 'Редактирование актива' : 'Новая запись'}</h2>
              <button type="button" className="secrets-close-btn" onClick={closeObservableAddModal} aria-label="Закрыть">
                ×
              </button>
            </div>
            <form className="secrets-form" onSubmit={handleObservableSubmit}>
              <div className="form-group">
                <label htmlFor="admin-observable-stock">Акция</label>
                <input
                  id="admin-observable-stock"
                  type="text"
                  value={observableForm.stock}
                  onChange={(event) => setObservableForm((previous) => ({ ...previous, stock: event.target.value }))}
                  placeholder="Тикер акции"
                  maxLength={20}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="admin-observable-futures">Фьючерс</label>
                <input
                  id="admin-observable-futures"
                  type="text"
                  value={observableForm.futures}
                  onChange={(event) => setObservableForm((previous) => ({ ...previous, futures: event.target.value }))}
                  placeholder="Тикер фьючерса"
                  maxLength={50}
                  required
                />
              </div>
              <div className="form-group administration-add-modal-checkbox">
                <label htmlFor="admin-observable-available">
                  <input
                    id="admin-observable-available"
                    type="checkbox"
                    checked={!observableForm.isHidden}
                    onChange={(event) =>
                      setObservableForm((previous) => ({ ...previous, isHidden: !event.target.checked }))
                    }
                  />
                  <span>Доступен</span>
                </label>
              </div>
              <div className="form-group administration-add-modal-checkbox">
                <label htmlFor="admin-observable-for-promo">
                  <input
                    id="admin-observable-for-promo"
                    type="checkbox"
                    checked={observableForm.forPromo}
                    onChange={(event) =>
                      setObservableForm((previous) => ({ ...previous, forPromo: event.target.checked }))
                    }
                  />
                  <span>Promo</span>
                </label>
              </div>
              <div className="secrets-modal-buttons">
                <button type="button" className="secrets-cancel-btn" onClick={closeObservableAddModal}>
                  Отмена
                </button>
                <button type="submit" className="secrets-save-btn">
                  {editingObservableId ? 'Сохранить' : 'Добавить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isDividendAddModalOpen && (
        <div className="secrets-modal-overlay" onSelectStart={preventModalOverlaySelection}>
          <div className="secrets-modal secrets-modal--calendar-popover">
            <div className="secrets-modal-header">
              <h2>{editingDividendId ? 'Редактирование дивиденда' : 'Новый дивиденд'}</h2>
              <button type="button" className="secrets-close-btn" onClick={closeDividendAddModal} aria-label="Закрыть">
                ×
              </button>
            </div>
            <form className="secrets-form" onSubmit={handleDividendSubmit} noValidate>
              <div className="form-group">
                <label htmlFor="admin-dividend-stock">Акция</label>
                <input
                  id="admin-dividend-stock"
                  type="text"
                  className={dividendFormErrors.stock ? 'administration-input-error' : ''}
                  value={dividendForm.stock}
                  onChange={(event) => handleDividendFormChange('stock', event.target.value)}
                  placeholder="Тикер акции"
                  maxLength={20}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="admin-dividend-size">Размер</label>
                <input
                  id="admin-dividend-size"
                  type="number"
                  className={dividendFormErrors.size ? 'administration-input-error' : ''}
                  step="0.0001"
                  value={dividendForm.size}
                  onChange={(event) => handleDividendFormChange('size', event.target.value)}
                  placeholder="Размер дивиденда"
                />
              </div>
              <div className="form-group">
                <label htmlFor="admin-dividend-date">Дата</label>
                <div className="secrets-input-with-clear write-order-date-field" ref={dividendDatePickerRef}>
                  <input
                    type="text"
                    id="admin-dividend-date"
                    value={formatDateForModalInput(dividendForm.date)}
                    readOnly
                    onClick={() => setIsDividendDatePickerOpen(true)}
                    onFocus={() => setIsDividendDatePickerOpen(true)}
                    className={modalInputClass({
                      withActions: true,
                      clickable: true,
                      readonly: true,
                      error: Boolean(dividendFormErrors.date),
                    })}
                    placeholder="дд.мм.гггг"
                  />
                  <div className="secrets-input-actions">
                    <button
                      type="button"
                      onClick={() => setIsDividendDatePickerOpen((previous) => !previous)}
                      className="secrets-input-action-btn"
                      aria-label="Открыть календарь"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDividendFormChange('date', dividendDateDefault)}
                      className="secrets-input-action-btn"
                      aria-label="Восстановить дату по умолчанию"
                      disabled={dividendForm.date === dividendDateDefault}
                    >
                      ↺
                    </button>
                  </div>
                  {isDividendDatePickerOpen && (
                    <div className="write-order-date-popover">
                      <ModernDatePicker
                        value={dividendForm.date}
                        className="modern-date-picker--light modern-date-picker--popover"
                        onChange={(date) => {
                          handleDividendFormChange('date', date);
                          setIsDividendDatePickerOpen(false);
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="admin-dividend-url">Источник</label>
                <input
                  id="admin-dividend-url"
                  type="url"
                  className={dividendFormErrors.url ? 'administration-input-error' : ''}
                  value={dividendForm.url}
                  onChange={(event) => handleDividendFormChange('url', event.target.value)}
                  placeholder="URL источника"
                />
              </div>
              <div className="form-group administration-add-modal-checkbox">
                <label htmlFor="admin-dividend-approved">
                  <input
                    id="admin-dividend-approved"
                    type="checkbox"
                    checked={dividendForm.approved}
                    onChange={(event) =>
                      handleDividendFormChange('approved', event.target.checked)
                    }
                  />
                  <span>Рекомендован или утвержден</span>
                </label>
              </div>
              <div className="form-group administration-add-modal-checkbox">
                <label htmlFor="admin-dividend-dont-take">
                  <input
                    id="admin-dividend-dont-take"
                    type="checkbox"
                    checked={dividendForm.dontTake}
                    onChange={(event) =>
                      handleDividendFormChange('dontTake', event.target.checked)
                    }
                  />
                  <span>Не брать в расчет</span>
                </label>
              </div>
              <div className="secrets-modal-buttons">
                <button type="button" className="secrets-cancel-btn" onClick={closeDividendAddModal}>
                  Отмена
                </button>
                <button type="submit" className="secrets-save-btn">
                  {editingDividendId ? 'Сохранить' : 'Добавить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={Boolean(confirmDialog)}
        onClose={() => setConfirmDialog(null)}
        onConfirm={confirmDialog?.onConfirm ?? (() => {})}
        title={confirmDialog?.title}
        message={confirmDialog?.message ?? ''}
        confirmLabel={confirmDialog?.confirmLabel}
        confirmVariant={confirmDialog?.confirmVariant}
      />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </div>
  );
}

function App() {
  const [isSecretsModalOpen, setIsSecretsModalOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    captureReferralFromUrl();

    // Проверяем авторизацию при загрузке
    const checkAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          const profile = await authService.getProfile();
          setUser(profile);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Auth check failed:', error);
          authService.logout();
          setIsAuthenticated(false);
          setUser(null);
        }
      }
    };

    checkAuth();
  }, []);

  const handleLogin = async (email, password) => {
    await authService.login(email, password);
    const profile = await authService.getProfile();
    setUser(profile);
    setIsAuthenticated(true);
  };

  const handleRegister = async (email, password) => {
    await authService.register(email, password);
    const profile = await authService.getProfile();
    setUser(profile);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  const isAdmin = user?.group === 'admin';
  const isPromo = user?.group === 'promo';

  const authenticatedRoutes = (
    <PlanStreamProvider userId={user?.id} userGroup={user?.group}>
      <Routes>
        <Route path="/" element={<AppTable />} />
        <Route path="/deals" element={isPromo ? <Navigate to="/" replace /> : <DealsTable />} />
        <Route
          path="/account-robot"
          element={isPromo ? <Navigate to="/" replace /> : <AccountRobotRoute />}
        />
        <Route
          path="/administration"
          element={isAdmin ? <AdministrationPage /> : <Navigate to="/" replace />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </PlanStreamProvider>
  );

  return (
    <ThemeProvider>
      <Router>
        {isAuthenticated ? (
          <div className="app-shell">
            <AppNav
              user={user}
              isAdmin={isAdmin}
              isPromo={isPromo}
              onLogout={handleLogout}
              onOpenSecrets={() => setIsSecretsModalOpen(true)}
            />

            <main className="app-main">
              {isPromo ? authenticatedRoutes : (
                <RobotCommandsProvider>
                  {authenticatedRoutes}
                </RobotCommandsProvider>
              )}
            </main>
          </div>
        ) : (
          <Routes>
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="*"
              element={<WelcomePage onLogin={handleLogin} onRegister={handleRegister} />}
            />
          </Routes>
        )}
        <SecretsModal
          isOpen={isSecretsModalOpen}
          onClose={() => setIsSecretsModalOpen(false)}
        />
      </Router>
    </ThemeProvider>
  );
}

export default App;
